/**
 * RWBR — ONE TURN, green to concluded (rulebook p.ii–p.xv).
 *
 * "Most turns have two parts to them. The first part is the 'Race Cam' phase,
 *  which captures the dominant moment(s) of that 10-mile stretch of the race.
 *  The second part of the turn is the 'Action' phase, where you resolve these
 *  moment(s). The exceptions to this are PIT turns and YELLOW flag turns."
 *                                                              — p.ii
 *
 * This module owns the SHAPE of a turn and nothing else. Every rule it needs is
 * already written somewhere: challenges in challenge.ts, duels in duel.ts,
 * tempers in feudFume.ts, incidents in trouble/problem/rare.ts, the boards in
 * fillGap.ts, pit road in pitTurn.ts. What lives here is the order of operations
 * and the one thing no single rule module can do — apply a result to the field.
 *
 *   1. What kind of turn is this?  (p.xi — YELLOW FLAG TURNS, PIT TURNS)
 *   2. RACE CAM: black die + metallic total, read on the track card. (p.ii)
 *   3. Dispatch to the rule the card named.                          (p.iii-x)
 *   4. MULTIPLE EVENTS on metallic doubles.                          (p.iii)
 *   5. FILL A GAP.                                                   (p.vi)
 *   6. CONCLUDE: laps led, chips, the caution clock.                 (p.xi, p.xv)
 *
 * PURITY. The `RaceState` handed in is never mutated. A new one comes back with
 * the turn number already advanced.
 */

import type { Car, Group, PitCrewGrade, Quality, RaceCamResult, RaceGrants, RaceState } from './types.ts';
import type { CareerConsequence } from './careerConsequences.ts';
import type { RareResult } from './rare.ts';
import {
  CITE_HOUSE,
  CLEAN_AIR_BRAKE_BONUS,
  PRINTED_GAME,
  PACE_WINDOW,
  paceLapAfter,
  resolveTrackTemper,
  trackTemperFor,
  type HouseRulesInForce,
} from './houseRules.ts';
import { QUALITIES, groupOf } from './types.ts';
import type { Dice } from './dice.ts';
import { markResolved } from './diceTrace.ts';
import type { ChipDelta, DiceRecord, Modifier, PendingTrouble, PositionDelta, RuleEvent } from './ruleEvent.ts';
import { fieldFromState, renumber, stampDiceEntry } from './ruleEvent.ts';
import type { ChipHold } from './chips.ts';
import {
  CITE_RESERVE,
  CITE_BG_CHIP_LOST,
  CITE_TRAIT_CHIPS,
  activateReserve,
  awardTraitChip,
  describeHolds,
  gainChip,
  parkChips,
  reserveChipsOf,
  voidChips,
} from './chips.ts';
import type { TrackAdjustment } from './repair.ts';
import { resolveTrouble, troubleFieldFromGrid, type ChipPolicy, type TroubleField } from './trouble.ts';
import { grantsAreEmpty } from './interviewEffects.ts';
import type { InjuryResult } from './injuries.ts';
import {
  CITE_NOTE_3,
  resolveRestartEvent,
  restartDuelOffer,
  restartPositionDeltas,
  rollRseTrigger,
  type RestartDuelPolicy,
  type RestartEventResult,
} from './restartEvent.ts';
import {
  ChurnTracker,
  CITE_BGC_DEFERRED_YELLOW,
  CITE_BGC_INCIDENTS,
  CITE_BGC_INCREMENT,
  isChurnTurn,
  resolveBottomGroupChurn,
  rollChurnTrigger,
  type BottomGroupChurnResult,
} from './bottomGroupChurn.ts';
import {
  CITE_BGS_ORDER,
  SurgeTracker,
  isSurgeTurn,
  resolveBottomGroupSurge,
  rollSurgeTrigger,
  type BottomGroupSurgeResult,
} from './bottomGroupSurge.ts';
import { resolveProblem } from './problem.ts';
import { raceThird } from './repair.ts';
import { isSuperSpeedway } from './drafting.ts';
import { ChartBTracker, trackTypeOf } from './troubleMatrix.ts';
import { rollDriverFinder } from './problem.ts';
import { adjustmentValue as adjValue } from './repair.ts';
import { resolveRare } from './rare.ts';
import { resolveQualityChallenges, type CalledGroup } from './challenge.ts';
import { runDraftPhase } from './draftPhase.ts';
import { callDuel, defaultDuelTarget, resolveDuel, type DuelResult } from './duel.ts';
import { resolveFeudFume } from './feudFume.ts';
import { fillGaps, type Vacancies, type Leaver, type LeaveReason } from './fillGap.ts';
import { resolvePitTurn, type PitTurnResult } from './pitTurn.ts';
import {
  CITE_LD,
  LapsDownTracker,
  awardLuckyDog,
  ldsgStart,
  settleLapsDown,
  waveAround,
} from './lapsDown.ts';
import trackCardsJson from '../data/track-cards.json';

// ---------------------------------------------------------------------------
// Track cards
// ---------------------------------------------------------------------------

export interface TrackFootnote {
  marker: string;
  /** The printed cell(s) the dagger hangs off, e.g. ["REFLEX"]. */
  appliesTo: string[];
  /**
   * THE METALLIC TOTAL the dagger is printed against, as a string — "3", "11".
   *
   * ⚠ ADDED FOR THE 2025 SET, WHERE `appliesTo` STOPPED BEING ENOUGH. The
   * Autodromo Hermanos Rodriguez card prints HEAT twice: on a 3 with the burst
   * symbol, and on an 8 with the dagger. Looked up by printed RESULT, the
   * "use SMART on last three turns" override fires on both — a rule the card
   * applies to one cell, applied by us to two.
   *
   * Optional, because the 2000s and 2014 cards have no such collision and are
   * still keyed the old way.
   */
  cell?: string;
  text: string;
}

/**
 * THE BURST — the ✻ printed beside a RACE CAM cell on the 2025 cards.
 *
 * D-EZ-75. New to the 2025 Pro Season set and to this engine: a cell wearing
 * the burst resolves normally AND THEN adds a PROBLEM. The cards print it three
 * ways, and all three are here because all three are on real cards:
 *
 *   • "add PROBLEM to end of turn."          — every card but two
 *   • "add PROBLEM to end of turn, last ten turns."  — Iowa, Watkins Glen
 *   • "add PROBLEM to start of turn, then resolve TROUBLE."  — Chicago only
 *
 * Chicago is the one that makes `when` a field rather than a boolean: its
 * burst cell IS a TROUBLE, and the card says the PROBLEM happens FIRST. The
 * 2025 roster notes call this out explicitly — *"there's a special 'burst'
 * symbol that signals a PROBLEM happens at the end of the turn. NOTE that
 * there is a PROBLEM result that occurs on the #10 dice roll at Chicago. Here
 * the PROBLEM is resolved at the start of the turn, BEFORE the TROUBLE
 * result."*
 */
export interface TrackBurst {
  /** The metallic totals the ✻ is printed against. */
  cells: string[];
  when: 'end-of-turn' | 'start-of-turn';
  /** Only in the closing turns, where the card says so. */
  lastTurns?: number;
  /** The card's own wording, for the receipt. */
  text: string;
}

export interface TrackCard {
  id: string;
  name: string;
  category: string;
  location?: string | null;
  raceName?: string | null;
  /** The suggested race length in turns; one turn = 10 miles (p.ii). */
  turns: number;
  lengthMiles?: number | null;
  /**
   * Grandstand capacity as printed on the card. Null on the three road courses,
   * which print none. Nothing in the race engine reads it — it exists because
   * Sagliano's FAN ATTENDANCE chart (SAG/22) is a percentage OF capacity, and
   * the gate is where the track purse comes from.
   */
  capacity?: number | null;
  escapeAdj: TrackAdjustment;
  repairAdj: TrackAdjustment;
  groupDice: { top: number[]; middle: number[]; bottom: number[]; duel: number[] };
  /** The RACE CAM column, keyed by the metallic total "2".."12". */
  raceCam: Record<string, string>;
  footnotes?: TrackFootnote[];
  /** The ✻ cells. Absent on every card printed before 2025. */
  burst?: TrackBurst;
  /** A note printed against the TURN COUNT — Chicago's "100 mile race rules". */
  raceNote?: string | null;
  /** The physical racetrack. See `schedule.ts` — one card per venue per season. */
  venue?: string;
  cardYear?: number;
  /** Which race(s) of the printed 2025 calendar this card hosts. */
  raceNumbers?: number[];
}

export const TRACK_CARDS = trackCardsJson as unknown as TrackCard[];

export function trackCard(id: string): TrackCard {
  const card = TRACK_CARDS.find((t) => t.id === id);
  if (!card) throw new RangeError(`no track card with id "${id}"`);
  return card;
}

// ---------------------------------------------------------------------------
// Reading the RACE CAM
// ---------------------------------------------------------------------------

export type CamGroup = 'top' | 'middle' | 'bottom' | 'duel';

const WORD_NUMBERS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
};

function numberWord(word: string): number | null {
  const w = word.trim().toLowerCase();
  if (/^\d+$/.test(w)) return Number(w);
  return WORD_NUMBERS[w] ?? null;
}

/**
 * The printed cell with its symbols stripped off.
 *
 * ⚠ `✻` JOINED THE LIST AT D-EZ-75 AND THE OMISSION WAS NOT SUBTLE. The moment
 * the 2025 cards went into the library every season died on
 * `unhandled RACE CAM result "REFLEX✻"` — 624 tests in one run. A symbol the
 * reader does not know about is not ignored; it becomes part of the result.
 */
export function bareResult(cell: string): RaceCamResult {
  return cell.replace(/[†*✻]/g, '').trim().toUpperCase() as RaceCamResult;
}

export interface RaceCamRead {
  black: number;
  metallic: [number, number];
  total: number;
  doubles: boolean;
  group: CamGroup;
  /** Exactly what the card prints in that cell, dagger and all. */
  printed: string;
  /** What is actually resolved, after any footnote override. */
  result: RaceCamResult;
  footnote: string | null;
  overridden: boolean;
  /**
   * THE BURST FIRED — D-EZ-75. Null on every card printed before 2025 and on
   * any 2025 cell without the ✻, or with it but outside its turn window.
   *
   * `when` is carried through because Chicago's burst resolves BEFORE the cell
   * and everybody else's resolves after, and a caller that flattened it to a
   * boolean would have to guess.
   */
  burst: { when: 'end-of-turn' | 'start-of-turn'; text: string } | null;
}

export interface RaceCamContext {
  turn: number;
  totalTurns: number;
  /**
   * How many cars in the called group hold a named quality. Only the Talladega
   * dagger needs it ("If more than two drivers in group with HEAT quality, use
   * TROUBLE instead."), and it is optional so the reader stays usable without a
   * field in hand.
   */
  qualityCount?: (quality: Quality, group: CamGroup) => number;
}

/** Which group the black die pointed at, per the track card's own printing. */
export function camGroup(track: TrackCard, black: number): CamGroup {
  const g = track.groupDice;
  if (g.top.includes(black)) return 'top';
  if (g.middle.includes(black)) return 'middle';
  if (g.bottom.includes(black)) return 'bottom';
  if (g.duel.includes(black)) return 'duel';
  throw new RangeError(`black die ${black} is not on ${track.id}'s RACE CAM group row`);
}

/**
 * Read one RACE CAM cell, honouring the track card's dagger footnote.
 *
 * Two kinds of footnote are printed across the 31 cards:
 *   • a late-race override — "Use SKILL on last five turns." / "Use SMART on
 *     final two turns of race." / "Use TROUBLE on last 10 turns."
 *   • one conditional — Talladega's "If more than two drivers in group with HEAT
 *     quality, use TROUBLE instead."
 *
 * AMBIGUITY: "on last five turns" is read inclusively against the SCHEDULED
 * length — turns 46-50 of a 50-turn race. A race extended past its scheduled
 * length because it cannot end under yellow (p.xv) stays inside the window.
 */
export function readRaceCam(
  track: TrackCard,
  roll: { black: number; metallic: [number, number]; total: number; doubles: boolean },
  ctx: RaceCamContext,
): RaceCamRead {
  const group = camGroup(track, roll.black);
  const printed = track.raceCam[String(roll.total)];
  if (printed === undefined) {
    throw new RangeError(`${track.id}: no RACE CAM cell for a metallic total of ${roll.total}`);
  }

  let result = bareResult(printed);
  let footnote: string | null = null;
  let overridden = false;

  const dagger = /[†*]/.test(printed);
  if (dagger) {
    /*
      ⚠ BY CELL FIRST, BY RESULT ONLY AS A FALLBACK — D-EZ-75.

      The Autodromo Hermanos Rodriguez card prints HEAT on a 3 (with the burst)
      and HEAT on an 8 (with the dagger). Matching on the printed RESULT fires
      the 8's "use SMART on last three turns" override on a 3 as well: a rule
      the card gives to one cell, applied by us to two. The 2025 cards therefore
      carry the metallic total on the footnote; the older sets have no such
      collision and still match the way they always did.
    */
    const cell = String(roll.total);
    const notes = track.footnotes ?? [];
    const note =
      notes.find((f) => f.cell === cell) ??
      (notes.some((f) => f.cell !== undefined)
        ? undefined
        : notes.find((f) => f.appliesTo.some((a) => bareResult(a) === result)));
    if (note) {
      footnote = note.text;

      const late = /use\s+([A-Z]+)\s+on\s+(?:the\s+)?(?:last|final)\s+([a-z0-9]+)\s+turns?/i.exec(note.text);
      if (late) {
        const n = numberWord(late[2]);
        if (n !== null && ctx.turn > ctx.totalTurns - n) {
          result = bareResult(late[1]);
          overridden = true;
        }
      }

      const conditional =
        /if\s+more\s+than\s+([a-z0-9]+)\s+drivers?\s+in\s+group\s+with\s+([A-Z]+)\s+quality,\s*use\s+([A-Z]+)\s+instead/i.exec(
          note.text,
        );
      if (conditional && ctx.qualityCount) {
        const threshold = numberWord(conditional[1]);
        const quality = bareResult(conditional[2]);
        if (threshold !== null && group !== 'duel') {
          const held = ctx.qualityCount(quality as Quality, group);
          if (held > threshold) {
            result = bareResult(conditional[3]);
            overridden = true;
          }
        }
      }
    }
  }

  /*
    THE BURST. Read from the card, gated on the card's own turn window, and
    reported rather than resolved — `turn.ts`'s caller owns when a PROBLEM
    happens, and this function's job has always been to say what the cell says.
  */
  let burst: RaceCamRead['burst'] = null;
  if (track.burst && /✻/.test(printed) && track.burst.cells.includes(String(roll.total))) {
    const window = track.burst.lastTurns;
    const inWindow = window === undefined || ctx.turn > ctx.totalTurns - window;
    if (inWindow) burst = { when: track.burst.when, text: track.burst.text };
  }

  return {
    black: roll.black,
    metallic: roll.metallic,
    total: roll.total,
    doubles: roll.doubles,
    group,
    printed,
    result,
    footnote,
    overridden,
    burst,
  };
}

/** Is this cell a QUALITY challenge call rather than an incident? */
export function isQualityCall(result: RaceCamResult): result is Quality | 'TV' {
  return result === 'TV' || (QUALITIES as readonly string[]).includes(result);
}

// ---------------------------------------------------------------------------
// Applying a result to the field
// ---------------------------------------------------------------------------

export type TurnKind = 'green' | 'yellow' | 'pit';

interface Working {
  order: Car[];
  retired: Car[];
  /**
   * The board group each card started the turn in.
   *
   * AMBIGUITY: p.vi fills holes at the END of the turn, so a hole opened on the
   * first incident of a turn is still open when the second one is resolved. This
   * engine closes the running order up as cards leave, which is the same field
   * order but not the same board — a MIDDLE car can be sitting in a TOP slot
   * before the fill has been rolled for. Vacancies are therefore charged to the
   * group the card started the turn in, which keeps the count inside the printed
   * capacities (one LEADER, six TOP, twelve MIDDLE) no matter how many incidents
   * a single turn produces.
   */
  startGroup: Map<string, Group>;
  /**
   * Cards whose starting slot has already been counted as open this turn.
   *
   * BUG FIX (found by the season loop, 36 races x N seasons): without this,
   * a card could charge its starting group TWICE in one turn — once when a feud
   * or a problem sent it to the END of the BOTTOM stack, and again when the
   * TROUBLE that followed wrecked it out of the stack. Three cars doing that in
   * one Daytona turn charged the six-seat TOP group eight vacancies and
   * `fillGap.ts` threw, which is exactly the guard doing its job. A card leaves
   * one hole in the board however many times it is moved afterwards; the second
   * move happens in the BOTTOM stack, which has no fixed slots to vacate.
   */
  vacatedBy: Set<string>;
  vacated: { leader: number; top: number; middle: number };
  /**
   * D-EZ-69 — WHO LEFT THE BOARD THIS TURN, AND WHAT HAPPENED TO HIM.
   *
   * Howard, 20 Aug 2026, on the fill-a-gap line: *"The Cars off the board notice
   * here isnt really helpful in that regard … Who got knocked out and why?"*
   *
   * The counters in `vacated` are all `fillGaps` ever had, so it could name the
   * men who moved UP and never the ones whose leaving made the room. "2 open
   * spaces on the boards" is the shape of the news with the news taken out.
   *
   * ⚠ RECORDED AT THE MOMENT OF THE MOVE, in `noteVacancy`, because that is the
   * only place that still knows which group he was in and what put him there.
   * By the time the gap is filled he is in the BOTTOM stack or in `retired`, and
   * his old slot is somebody else's.
   */
  vacatedWhy: Leaver[];
  chipDeltas: ChipDelta[];
  /**
   * Trait chips parked and handed back this turn (EZRA-S2 §4.0). Kept apart
   * from `chipDeltas` on purpose: nothing was created or destroyed, and a
   * suspension filed as a -1 would read in the ledger as exactly the forfeit
   * the EZRA rule exists to prevent.
   */
  chipHolds: ChipHold[];
  feuds: RaceState['feuds'];
  fuming: string[];
  /**
   * The race's laps-down memory, or null when the optional rule is off.
   *
   * Carried on the working field rather than passed to `applyDeltas` because
   * "who went a lap down, and on which turn" has to be recorded at the instant
   * the card moves — the Lucky Dog's one-complete-turn bar is unanswerable
   * afterwards.
   */
  lapsDown: LapsDownTracker | null;
  /** The turn being resolved, for the tracker's bookkeeping. */
  turnNo: number;
  /**
   * Cars in the incident currently being folded in, WHEN that incident is the
   * one raising the yellow. A car that goes a lap down here is a car the
   * caution is about, and EZRA bars exactly those from the Lucky Dog. Set by
   * `absorb` immediately before it applies the deltas and cleared after.
   */
  yellowCause: Set<string>;
}

function positionIn(order: readonly Car[], driverId: string): number {
  return order.findIndex((c) => c.card.driverId === driverId) + 1;
}

/**
 * ⚠ EVERY CALLER HANDS OVER THE NAME. This runs immediately after the card has
 * been spliced out of the order and usually before it lands anywhere else, so
 * there is a moment when it is in no list at all and a lookup here would fall
 * back to a driver id. A ledger line reading "drv-036 is out of the race" is
 * the kind of thing that ships.
 */
function noteVacancy(
  w: Working,
  driverId: string,
  livePosition: number,
  why: LeaveReason,
  name: string,
): void {
  if (w.vacatedBy.has(driverId)) return;
  const g = w.startGroup.get(driverId) ?? groupOf(livePosition);
  if (g === 'leader' || g === 'top' || g === 'middle') {
    w.vacated[g] += 1;
    w.vacatedBy.add(driverId);
    w.vacatedWhy.push({ driverId, name, from: g, position: livePosition, why });
  }
}

/** The out-of-race reasons, as this line needs to say them. */
function whyOut(reason: Car['outReason']): LeaveReason {
  switch (reason) {
    case 'trouble':
      return 'wreck';
    case 'feud':
      return 'feud';
    case 'fuel':
      return 'fuel';
    case 'black-flag':
      return 'black-flag';
    case 'repair-failed':
      return 'repair-failed';
    case 'injury':
      return 'injury';
    default:
      // 'problem', and anything a later sheet adds: "out of the race with a
      // problem" is true of every one of them and invents nothing.
      return 'problem';
  }
}

/**
 * A card is going into the BOTTOM group or a lap down.
 *
 * Ordinary chips are destroyed (p.xv). Chips a TRAIT awarded are parked, not
 * destroyed (EZRA-S2 §4.0) — so this is not a plain wipe, and the ledger gets
 * two different lines depending on which happened. Done at the moment of the
 * move rather than left to `renumber` so the ledger reads in the order a person
 * moving the cards would have seen it.
 */
function forfeitChips(w: Working, car: Car, reason: string): Car {
  const moved = parkChips(car);
  if (moved.forfeited > 0) {
    w.chipDeltas.push({ driverId: car.card.driverId, delta: -moved.forfeited, reason });
  }
  if (moved.parked > 0) {
    w.chipHolds.push({ driverId: car.card.driverId, chips: moved.parked, restored: false });
  }
  return moved.car;
}

/** A card is OUT. Everything is void, parked trait chips included. */
function voidCarChips(w: Working, car: Car, reason: string): Car {
  const moved = voidChips(car);
  if (moved.forfeited > 0) {
    w.chipDeltas.push({ driverId: car.card.driverId, delta: -moved.forfeited, reason });
  }
  return moved.car;
}

/**
 * Apply one rule's position deltas to the working field.
 *
 * `placement` is authoritative when the incident modules set it, because they
 * are describing a slot they cannot count ("the END of the BOTTOM group"). When
 * it is absent, `to` is authoritative — challenges, duels and feuds compute
 * their own slots.
 *
 * AMBIGUITY: a RARE row that moves one car and re-seats another (the SURGE, p.
 * RARE RESULTS sheet) hands over two deltas whose target slots were computed
 * against the board BEFORE either move. They are applied in the order printed,
 * against the board as it stands, which is what a person moving cards by hand
 * would do.
 */
function applyDeltas(w: Working, deltas: readonly PositionDelta[], turn: number, outReason: Car['outReason']): void {
  for (const delta of deltas) {
    const i = w.order.findIndex((c) => c.card.driverId === delta.driverId);
    if (i === -1) continue; // already out of the race earlier this turn
    const car = w.order[i];
    const livePosition = i + 1;
    const placement = delta.placement ?? (delta.to === null ? 'out-of-race' : undefined);

    if (placement === 'out-of-race') {
      w.order.splice(i, 1);
      // ⚠ AFTER the splice and BEFORE the push into `retired`, the card is in
      // neither list — so the name is read off the car we already hold.
      noteVacancy(w, delta.driverId, livePosition, whyOut(outReason), car.card.name);
      const stripped = voidCarChips(w, car, 'out of the race — chips are void (p.xv)');
      w.retired.push({ ...stripped, status: 'out', outReason, outTurn: turn, position: livePosition });
      continue;
    }

    if (placement === 'laps-down') {
      w.order.splice(i, 1);
      noteVacancy(w, delta.driverId, livePosition, 'lap-down', car.card.name);
      const stripped = forfeitChips(w, car, 'sent to the BOTTOM group — chips are forfeited (p.xv)');
      w.order.push({ ...stripped, lapsDown: true });
      // `causedTheCaution` is decided by the caller through `w.yellowCause`,
      // because only the incident knows whether the yellow was ITS doing.
      w.lapsDown?.wentDown(delta.driverId, w.turnNo, w.yellowCause.has(delta.driverId));
      continue;
    }

    if (placement === 'last-of-lead-lap') {
      // Splice out first, THEN read the boundary — removing a lapped car moves
      // it, and inserting against a stale index puts the car on the wrong side
      // of its own marker.
      w.order.splice(i, 1);
      noteVacancy(w, delta.driverId, livePosition, 'to-the-back', car.card.name);
      const stripped = forfeitChips(w, car, 'sent to the BOTTOM group — chips are forfeited (p.xv)');
      const to = Math.min(ldsgStart(w.order), w.order.length + 1);
      w.order.splice(to - 1, 0, { ...stripped, lapsDown: false });
      if (car.lapsDown) w.lapsDown?.cameBack(delta.driverId);
      continue;
    }

    if (placement === 'end-of-bottom' || placement === 'front-of-bottom') {
      w.order.splice(i, 1);
      noteVacancy(w, delta.driverId, livePosition, 'to-the-back', car.card.name);
      const stripped = forfeitChips(w, car, 'sent to the BOTTOM group — chips are forfeited (p.xv)');
      if (placement === 'front-of-bottom') {
        w.order.splice(Math.min(19, w.order.length), 0, stripped);
      } else if (stripped.lapsDown) {
        // Already a lap down: the end of HIS group is the end of the field.
        w.order.push(stripped);
      } else {
        // READING: with the BOTTOM group split, "the END of the BOTTOM group"
        // for a car still on the lead lap is the last LEAD-LAP position, not
        // the back of the field. EZRA says so itself where it has to place a
        // penalised car with the split in force: restart violations send
        // "Others to the last position in the Bottom Group Lead Lap Sub Group."
        // Sorting a car that never lost a lap below cars that did would also
        // break the King-of-the-Hill ranking the whole design rests on.
        const to = Math.min(ldsgStart(w.order), w.order.length + 1);
        w.order.splice(to - 1, 0, stripped);
      }
      continue;
    }

    if (placement === 'lead') {
      w.order.splice(i, 1);
      w.order.unshift(car);
      continue;
    }

    // 'hold' with a slot that actually moved, 'top-group', 'middle-group', or a
    // bare numeric `to` from a challenge/duel/feud.
    if (delta.to !== null && delta.to !== livePosition) {
      w.order.splice(i, 1);
      const target = Math.max(0, Math.min(delta.to - 1, w.order.length));
      w.order.splice(target, 0, car);
      if (groupOf(delta.to) === 'bottom') {
        const j = w.order.findIndex((c) => c.card.driverId === delta.driverId);
        w.order[j] = forfeitChips(w, w.order[j], 'sent to the BOTTOM group — chips are forfeited (p.xv)');
      }
    }
  }
}

function applyChipDeltas(w: Working, deltas: readonly ChipDelta[]): void {
  for (const d of deltas) {
    const i = w.order.findIndex((c) => c.card.driverId === d.driverId);
    if (i === -1) continue;
    if (d.delta > 0) {
      // D-EZ-28 — "If a driver gains a PC while already holding 2 pc's, the
      // extra pc (or pc's) are held in reserve." Every gain in the engine funnels
      // through here or through pitTurn's `addChips`, so capping at these two
      // points caps all of them.
      const got = gainChip(w.order[i], d.delta);
      w.order[i] = got.car;
      if (got.toReserve > 0) {
        w.chipHolds.push({ driverId: d.driverId, chips: got.toReserve, restored: false, reserve: true });
      }
    } else {
      w.order[i] = { ...w.order[i], chips: Math.max(0, w.order[i].chips + d.delta) };
    }
    w.chipDeltas.push(d);
  }
}

/** A `RaceState` view of the working field, for the modules that want one. */
function viewState(state: RaceState, w: Working, turn: number): RaceState {
  return { ...state, turn, grid: renumber(w.order).order, retired: w.retired };
}


// ---------------------------------------------------------------------------
// EZRA driver traits that hand out a performance chip (EZRA-S2 §4.0)
// ---------------------------------------------------------------------------

/** [FN] — "In races that are 30 turns or more". */
export const FN_MIN_TURNS = 30;
/** [FN] — "with five (5) turns remaining in the race". */
export const FN_TURNS_REMAINING = 5;

export const CITE_FN =
  'EZRA-S2 §4.0 [FN] Finisher — "In races that are 30 turns or more, a Finisher receives a bonus ' +
  'Performance chip with five (5) turns remaining in the race."';
export const CITE_ST =
  'EZRA-S2 §4.0 [ST] Short Track Expert — "At the start of the 1st turn after the mid-point of a ' +
  'Short Track race this driver receives a bonus Performance chip."';

/**
 * The turn on which a Finisher is paid, or null if the race is too short.
 *
 * "With five turns remaining" is read INCLUSIVELY: on turn N-4 there are five
 * turns still to be run (N-4, N-3, N-2, N-1, N). Measured against the SCHEDULED
 * length, matching how `readRaceCam` already reads its "on last five turns"
 * footnotes — a race extended past its length because it cannot finish under
 * yellow (p.xv) does not pay a second chip, because the turn number it keys on
 * is behind it.
 */
export function fnAwardTurn(totalTurns: number): number | null {
  if (totalTurns < FN_MIN_TURNS) return null;
  return totalTurns - FN_TURNS_REMAINING + 1;
}

/**
 * The turn on which a Short Track Expert is paid.
 *
 * "The 1st turn after the mid-point": half of 40 is 20, so turn 21. On an odd
 * length the mid-point falls inside a turn and the first one AFTER it is the
 * next whole turn — 45 turns pays on 23.
 */
export function stAwardTurn(totalTurns: number): number {
  return Math.floor(totalTurns / 2) + 1;
}

export interface TraitChipAward {
  driverId: string;
  trait: 'FN' | 'ST';
  label: string;
  reason: string;
  citation: string;
}

/**
 * Which drivers are owed a trait chip at the start of THIS turn.
 *
 * Pure and exported so the rate can be measured without running a race — a
 * trait that pays on one turn out of fifty is exactly the kind of rule that can
 * be wired backwards and still look alive in a log.
 */
export function traitChipAwards(
  field: readonly Car[],
  ctx: { turn: number; totalTurns: number; category: string },
): TraitChipAward[] {
  const out: TraitChipAward[] = [];
  const fnTurn = fnAwardTurn(ctx.totalTurns);
  const stTurn = ctx.category === 'Short Track' ? stAwardTurn(ctx.totalTurns) : null;
  for (const car of field) {
    if (car.status !== 'running') continue;
    if (car.card.trait === 'FN' && fnTurn !== null && ctx.turn === fnTurn) {
      out.push({
        driverId: car.card.driverId,
        trait: 'FN',
        label: 'Finisher',
        reason: `FINISHER — ${FN_TURNS_REMAINING} turns to go (EZRA-S2 §4.0)`,
        citation: CITE_FN,
      });
    }
    if (car.card.trait === 'ST' && stTurn !== null && ctx.turn === stTurn) {
      out.push({
        driverId: car.card.driverId,
        trait: 'ST',
        label: 'Short Track Expert',
        reason: 'SHORT TRACK EXPERT — first turn past halfway (EZRA-S2 §4.0)',
        citation: CITE_ST,
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Options and result
// ---------------------------------------------------------------------------

export interface TurnOptions {
  /**
   * p.xv — ENDING THE RACE. On the final turn BOTTOM-group results are
   * disregarded and a black 5 checks the called quality in both MIDDLE and TOP.
   */
  finalTurn?: boolean;
  /** p.xiv solitaire auto-spend, passed to challenges and duels. */
  useChips?: boolean;
  /** p.xv THE BIG ONE cascade. Default on. */
  bigOne?: boolean;
  chipPolicy?: ChipPolicy;
  /** p.xiv — the leader spends a chip to hold the lead on a pit turn. */
  leaderSpendsChip?: boolean;
  /**
   * The laps-down sub-group (LDSG). Absent = the optional rule is off and the
   * BOTTOM group is one stack, exactly as the base rulebook has it.
   *
   * LIKE CHART B, THIS BELONGS TO THE RACE, NOT THE TURN. Three of its four
   * printed rules are memory rules — "D has to successfully Challenge twice",
   * "must spend at least one complete turn in lap down status", "consecutive
   * yellow flag turns count as one yellow flag event" — and a tracker rebuilt
   * each turn would answer all three wrongly while looking like it worked.
   * `race.ts` makes one and hands the same object down every turn.
   */
  lapsDown?: LapsDownTracker | null;
  /**
   * Zimmerman's per-PIT-CREW wave-around, on top of the Lucky Dog. OFF by
   * default under D-EZ-14 — EZRA-S2 has no wave-around, and D-EZ-1 settled that
   * EZRA governs where the two sheets overlap. Ignored unless `lapsDown` is on.
   */
  waveAround?: boolean;
  /**
   * D-015 — the EZRA Trouble Matrix's Chart B index card.
   *
   * IT BELONGS TO THE RACE, NOT THE TURN. Chart B rations trouble types across a
   * whole race ("Once the space(s) allocated to a trouble type are completely
   * marked off, you can't choose from that trouble category again until every
   * other space is completely marked off"), so a tracker created per turn would
   * be a tracker that never runs out and never rations anything — the feature
   * would be present, cost dice, and do nothing. `race.ts` makes one and passes
   * the same object down every turn.
   *
   * Absent = the base game's TROUBLE, unchanged.
   */
  troubleMatrix?: ChartBTracker | null;
  /** D-EZ-7 — EZRA Appendix B1 pit violations. Default ON. */
  pitViolations?: false;
  /** EZRA-S2 §3.9 — Appendix B2 pit trouble on a Fast Pit roll of 12. Default ON. */
  pitTrouble?: false;
  /**
   * B2 row 2 — teams whose crewman was struck earlier in THIS race, so every
   * remaining Fast Pit check fails.
   *
   * IT BELONGS TO THE RACE, NOT THE TURN, for the same reason as the Chart B
   * tracker above: "all remaining Fast Pit checks in this race" is a race-long
   * claim, and a set rebuilt each turn would forget the injury between pit
   * stops and make the harshest row on the chart cost exactly one roll.
   * `race.ts` owns the array and passes the same one down every turn.
   */
  crewInjured?: string[];
  /**
   * D-EZ-16 — take the EZRA injury sheet's trigger literally ("fails their
   * Escape roll"), so a driver is checked for injury even when a performance
   * chip or the AW trait took him back out of the incident. Default OFF.
   *
   * Both readings are measured side by side in `scripts/interview-injuries.ts`,
   * because a ruling that changes how often drivers get hurt should be a number
   * rather than an argument.
   */
  injuryOnFailedRoll?: boolean;
  /**
   * D-EZ-22 — this turn is a RESTART TURN, so an EZRA Race Start Event may be
   * rolled for as a prelude. `race.ts` decides which turns qualify, because
   * only it knows the yellow-flag history.
   *
   * Absent or false = no prelude, which is the base game.
   */
  restartTurn?: boolean;
  /** Driver ids carrying the EZRA RS trait, which cancels a negative RSE. */
  restartExperts?: ReadonlySet<string>;
  /**
   * EZRA-S2 §4.0 RS — does THIS turn immediately follow a yellow flag turn?
   *
   * Narrower than `restartTurn`, and deliberately a separate flag: the Race
   * Start Event also fires on the first turn of a race, and the Restart Duel
   * does not — a race that has not started cannot "restart after a Yellow Flag".
   */
  followsYellow?: boolean;
  /** D-EZ-23 — whether the RS driver takes his free duel. Default 'auto'. */
  restartDuelPolicy?: RestartDuelPolicy;
  /**
   * EZRA-S2 §3.12 — the BOTTOM GROUP CHURN prelude (D-EZ-29). Absent or null =
   * the rule is off, which is the base game: there is no prelude phase at all.
   *
   * IT BELONGS TO THE RACE, NOT THE TURN, and that is the whole reason it is an
   * object rather than a boolean. "A BGC can occur only once each one third
   * race turn increment" is a memory rule, and a tracker rebuilt each turn
   * would enforce nothing while looking exactly like it worked. `race.ts` makes
   * one and hands the same object down every turn.
   */
  churn?: ChurnTracker | null;
  /**
   * EZRA-S2 §3.13 — the BOTTOM GROUP SURGE prelude. Absent or null = the rule is
   * OFF, **which is the default**, because §3.13's trigger face is not printed
   * anywhere in the corpus (⚠ OPEN RULING D-EZ-35, `bottomGroupSurge.ts`).
   *
   * A tracker rather than a boolean for two reasons. It COUNTS, because §3.13
   * prints no occurrence limit and a rule nobody counts cannot be told apart from
   * a rule that is not firing; and it carries the configured trigger faces, so
   * the object itself is the answer to "has D-EZ-35 been closed, and how".
   */
  surge?: SurgeTracker | null;
  /**
   * REALISM HOUSE RULES (D-EZ-83 … D-EZ-87), already resolved by `race.ts`.
   *
   * ABSENT = ALL OFF, the printed game — deliberately the opposite of the
   * race-level default. `runRace` turns them on unless refused; a caller that
   * drives `runTurn` by hand (a unit test, a scripted turn) gets the printed
   * rules it was written against. See `houseRules.ts`.
   */
  houseRules?: HouseRulesInForce;
  /** D-EZ-86 — this turn is the green-flag start or the first turn after a caution. */
  cleanAirTurn?: boolean;
  /** D-EZ-85 — consecutive green-flag turns completed before this one. */
  greenRunBefore?: number;
}

export interface TurnResult {
  /** Actual presentation throws; optional for older saved races. */
  diceReceipt?: import('./diceTrace.ts').SavedDiceReceipt;
  kind: TurnKind;
  turn: number;
  /** Every event of the turn, in the order it happened. */
  events: RuleEvent[];
  state: RaceState;
  raceCam: RaceCamRead | null;
  pit: PitTurnResult | null;
  yellowCameOut: boolean;
  yellowTurnsCalled: number;
  leadChangedTo: string | null;
  collected: string[];
  retiredThisTurn: string[];
  /**
   * Every EZRA injury rolled this turn, including the "no injury" reads. The
   * season layer needs the SERIOUS ones (they carry a 1d10 races-missed), and
   * the booth wants all of them — a driver climbing out shaken but unhurt is a
   * beat too.
   */
  injuries: InjuryResult[];
  /** The Race Start Event this turn's prelude produced, or null. */
  restart: RestartEventResult | null;
  /**
   * The Bottom Group Churn this turn's prelude produced, or null.
   *
   * Present for an ABORTED Churn too (`churn.occurred === false`). The two are
   * different facts — one spent its one-third increment and one did not — and a
   * caller that could not tell them apart could not check the limit.
   */
  churn: BottomGroupChurnResult | null;
  /**
   * The Bottom Group Surge this turn's prelude produced, or null — and null is
   * what it always is unless the rule was explicitly switched on (D-EZ-35).
   *
   * Present for an ABORTED Surge too (`surge.occurred === false`): a Surge whose
   * Driver Finder never found an occupied slot is a different fact from a Surge
   * that never triggered, and only the caller that can tell them apart can check
   * the four-roll rule.
   */
  surge: BottomGroupSurgeResult | null;
  /** The EZRA RS trait's extra restart duel, or null. */
  restartDuel: DuelResult | null;
  /**
   * Trait chips suspended and handed back this turn (EZRA-S2 §4.0).
   *
   * NOT chip deltas. Nothing was created or destroyed — that is the whole
   * content of the rule — and filing a suspension as a -1 would make the
   * ledger say the exact thing EZRA wrote the sentence to deny.
   */
  chipHolds: ChipHold[];
  /**
   * D-EZ-88 — SUPER RARE injuries, suspensions and a fatality from this turn,
   * for the season to apply. Optional only because older saved turns lack it.
   */
  careerConsequences?: CareerConsequence[];
  /**
   * D-EZ-88 — the race was called at the END of this turn (rain past halfway,
   * or the event cancelled). `race.ts` stops here and classifies the field as
   * it stands. Optional only because older saved turns lack it.
   */
  called?: RaceCalled | null;
}

/** D-EZ-88 — a race that ended before its scheduled length, and why. */
export interface RaceCalled {
  /** The last turn run. */
  turn: number;
  reason: 'rain' | 'cancelled';
  scheduledTurns: number;
  ruleCitation: string;
  /** The printed line that ended it. */
  text: string;
}

export const CITE_RARE_EFFECTS = 'RARE RESULTS sheet (rr_01.jpg) — D-EZ-88';
export const CITE_RAIN =
  'RARE RESULTS sheet, RAIN THREAT — "At the END that turn and end of each succeeding turn, roll one die. ' +
  'If a "1" or "6," then rain stops the event, and the current order is the final race finish, if enough ' +
  'laps have been completed." (D-EZ-88: "enough" ruled as half the scheduled distance)';
/** RAIN THREAT — the faces of the per-turn die on which the rain arrives. */
export const RAIN_STOPS_ON: readonly number[] = [1, 6];
/**
 * RAIN THREAT — "if enough laps have been completed". Not defined in the
 * corpus; ruled as the sport's own rule, half the scheduled distance (D-EZ-88).
 */
export const RAIN_OFFICIAL_FRACTION = 0.5;

const GRADES: readonly PitCrewGrade[] = ['A', 'B', 'C', 'D', 'E'];
/** "reduce PIT grade by one level" — A→B … D→E; E is the floor of the ladder. */
export function lowerGrade(grade: PitCrewGrade, levels = 1): PitCrewGrade {
  const i = GRADES.indexOf(grade);
  return GRADES[Math.min(GRADES.length - 1, Math.max(0, i) + levels)];
}

// ---------------------------------------------------------------------------
// The turn
// ---------------------------------------------------------------------------

export function runTurn(
  state: RaceState,
  track: TrackCard,
  dice: Dice,
  options: TurnOptions = {},
): TurnResult {
  const turn = state.turn;
  const totalTurns = state.totalTurns;
  const finalTurn = options.finalTurn ?? false;
  const isPit = state.pitTurns.includes(turn);
  const wasYellow = state.yellowTurnsRemaining > 0;
  const kind: TurnKind = isPit ? 'pit' : wasYellow ? 'yellow' : 'green';

  /**
   * D-018 — where this turn starts in the race's dice stream.
   *
   * Read again at the end, and the window between the two is stamped onto every
   * ledger row the turn produced (see the note by `stampDiceEntry` below). In a
   * seeded race `Dice.stream` is empty and this is 0 forever, which costs
   * nothing and says nothing.
   */
  const drawsAtTurnStart = dice.stream.length;
  dice.phase = `turn ${turn} — ${kind}`;

  // "Consecutive yellow flag turns count as one yellow flag event" — the tracker
  // has to be told what kind of turn this is BEFORE anything else, or a two-turn
  // caution hands out two Lucky Dogs.
  options.lapsDown?.startTurn(kind === 'yellow' || (isPit && wasYellow));

  const startOrder = fieldFromState(state);
  const leaderBefore = startOrder[0]?.card.driverId ?? null;

  const w: Working = {
    order: [...startOrder],
    retired: [...state.retired],
    startGroup: new Map(startOrder.map((c, i) => [c.card.driverId, groupOf(i + 1)])),
    vacatedBy: new Set<string>(),
    vacated: { leader: 0, top: 0, middle: 0 },
    vacatedWhy: [],
    chipDeltas: [],
    chipHolds: [],
    feuds: state.feuds.map((f) => ({ ...f })),
    fuming: [...state.fuming],
    lapsDown: options.lapsDown ?? null,
    turnNo: turn,
    yellowCause: new Set<string>(),
  };

  const events: RuleEvent[] = [];
  const record = (...resolved: RuleEvent[]): number => {
    markResolved(resolved, dice.rolls);
    return events.push(...resolved);
  };
  let raceCam: RaceCamRead | null = null;
  let pit: PitTurnResult | null = null;
  let yellowTurnsCalled = 0;
  let yellowCameOut = false;
  const collected: string[] = [];

  /** D-EZ-83 … D-EZ-87 — the realism house rules in force for this turn. */
  const house = options.houseRules ?? PRINTED_GAME;

  const troubleField = () => troubleFieldFromGrid(renumber(w.order).order);
  const incidentArgs = {
    turn,
    totalTurns,
    // D-EZ-87 — the era dial, read by `problem.ts` on a failed PROBLEM repair.
    era: house.era,
    escapeAdj: track.escapeAdj,
    repairAdj: track.repairAdj,
    // Read only by the EZRA SS trait, which is scoped to super-speedways.
    superSpeedway: isSuperSpeedway(track),
    // D-EZ-12 — a SUCCESSFUL repair costs a lap when the rule is in force.
    lapsDown: options.lapsDown != null,
    // D-EZ-16 — off by default; see injuries.ts for the ruling.
    injuryOnFailedRoll: options.injuryOnFailedRoll === true,
    // EZRA scopes the matrix to GROUP trouble; duels and feuds keep base-game
    // trouble, and get this by simply not being given `matrix`.
    ...(options.troubleMatrix
      ? {
          matrix: {
            tracker: options.troubleMatrix,
            track: trackTypeOf(track.category),
            escapeAdjustment: adjValue(track.escapeAdj),
            repairAdjustment: adjValue(track.repairAdj),
            driverFinder: (group: 'top' | 'middle' | 'bottom', size: number): number | null => {
              // EZRA: "Use the Driver Finder for the appropriate group to start
              // the involvement chain." The finder returns a car; we need its
              // index within the group, so the chain can run from there.
              const found = rollDriverFinder(group, troubleField(), dice);
              if (!found.car) return null;
              const idx = [...troubleField()[group]]
                .sort((a, b) => a.position - b.position)
                .findIndex((c) => c.driverId === found.car?.driverId);
              return idx < 0 || idx >= size ? null : idx;
            },
          },
        }
      : {}),
    dice,
    bigOne: options.bigOne,
    chipPolicy: options.chipPolicy,
  };

  /** Fold any incident event into the field and the caution clock. */
  /** Every injury rolled this turn, for the ledger and the season's carry. */
  const injuriesThisTurn: InjuryResult[] = [];
  /** D-EZ-88 — SUPER RARE results that outlive the race, dice already rolled. */
  const careerConsequences: CareerConsequence[] = [];
  /** D-EZ-88 — the turn at whose END the RAIN THREAT die starts to be rolled. */
  let rainCheckFrom: number | undefined = state.rainCheckFrom;
  /**
   * D-EZ-88 — the race was called (rain, or the event cancelled) at the end of
   * this turn. A holder rather than a `let`, because it is written from inside
   * the nested rule helpers and TypeScript would otherwise keep it narrowed to
   * `null` at the return.
   */
  const raceEnd: { called: RaceCalled | null } = { called: null };

  const absorb = (
    event: RuleEvent & {
      yellowTurns?: number;
      yellowFlag?: boolean;
      collectedIds?: string[];
      outIds?: string[];
      feudPairs?: [string, string][];
      fumeIds?: string[];
      chipSavedIds?: string[];
      trouble?: { chipSavedIds: string[] } | null;
      grantsUsed?: { driverId: string; grant: string }[];
      /** A single-driver grant, as `problem.ts` and `challenge.ts` report it. */
      grantUsed?: string;
      /** D-EZ-76 — the green triangle turned a failed PROBLEM repair into a place. */
      dnfExempted?: true;
      driverId?: string | null;
      challengerId?: string | null;
      injuredOutIds?: string[];
      injuries?: InjuryResult[];
    },
    outReason: Car['outReason'],
  ): void => {
    record(event);
    // Mark the incident's cars as the cause BEFORE the cards move, because
    // `applyDeltas` is where a car is recorded as going a lap down and the
    // Lucky Dog bar is asked about that exact moment.
    w.yellowCause = event.yellowFlag
      ? new Set<string>([...(event.collectedIds ?? []), ...event.actorIds])
      : new Set<string>();
    applyDeltas(w, event.positionDeltas, turn, outReason);
    w.yellowCause = new Set<string>();

    /*
      THE GREEN THUMBS-UP TRIANGLE, SECOND HALF — D-EZ-76.

      `problem.ts` did the merciful part: a failed REPAIR on a PROBLEM result
      puts this driver at the top of the BOTTOM group instead of on the hook.
      The 2025 card charges for it, and this is where the bill lands —

        "...but lose their ability to challenge or defend for the rest of the
         race."

      ⚠ IT MUST BE SET *AFTER* `applyDeltas`, not before. The deltas are what
      move him into the BOTTOM group, and `applyDeltas` rebuilds the entries it
      touches; a flag written on the car beforehand goes out with the old copy.
      That is the same ordering trap the Lucky Dog bar sits in four lines up.

      `challenge.ts` reads `cannotRace` on both sides of a pass — see
      `eligibleChallengers` and the defender lookup. Nothing else consults it,
      which is deliberate: he is still racing, still scoring, still able to be
      caught in somebody else's trouble. He simply cannot fight.
    */
    if (event.dnfExempted && event.driverId) {
      const i = w.order.findIndex((c) => c.card.driverId === event.driverId);
      if (i !== -1) w.order[i] = { ...w.order[i], cannotRace: true };
    }
    // p.xiv — "A driver can use a performance chip to avoid trouble." The trouble
    // module reports who spent one; the chip itself is deducted here.
    for (const id of [...(event.chipSavedIds ?? []), ...(event.trouble?.chipSavedIds ?? [])]) {
      applyChipDeltas(w, [{ driverId: id, delta: -1, reason: 'chip spent to avoid trouble (p.xiv)' }]);
    }
    if (event.yellowFlag) {
      yellowCameOut = true;
      yellowTurnsCalled = Math.max(yellowTurnsCalled, event.yellowTurns ?? 1);
    }
    for (const id of event.collectedIds ?? []) collected.push(id);
    for (const pair of event.feudPairs ?? []) {
      if (!w.feuds.some((f) => (f.a === pair[0] && f.b === pair[1]) || (f.a === pair[1] && f.b === pair[0]))) {
        w.feuds.push({ a: pair[0], b: pair[1] });
      }
    }
    for (const id of event.fumeIds ?? []) if (!w.fuming.includes(id)) w.fuming.push(id);

    // A one-race interview grant that fires twice is not a grant. Struck off the
    // car the moment the module that spent it says so. Two shapes arrive here:
    // a LIST (trouble checks a whole group at once) and a SINGLE (a problem
    // lands on one driver), so both are normalised into the same loop.
    const spends = [
      ...(event.grantsUsed ?? []),
      ...(event.grantUsed && (event.driverId || event.challengerId)
        ? [{ driverId: (event.driverId ?? event.challengerId) as string, grant: event.grantUsed }]
        : []),
    ];
    for (const spent of spends) {
      // ⚠ SEARCH THE RETIRED CARS TOO, and this was found by counting.
      //
      // The strike used to look only in `w.order`, so when the very incident
      // that SPENT a grant also put its holder out of the race — a feud that
      // came off a first duel and ended in the wall — the lookup missed him and
      // the grant was never struck. Measured: 15 of 58 firings left an unspent
      // grant sitting on a retired car, which is why
      // `fired + stillHeld === issued` would not close.
      //
      // Nothing was double-spending, so this never produced a wrong RESULT. It
      // produced a wrong RECORD, on a car that had just been told it had spent
      // something — and a record that says a one-time grant is still in hand is
      // exactly the sort of thing the next reader builds a bug on top of.
      const list = w.order.some((c) => c.card.driverId === spent.driverId) ? w.order : w.retired;
      const i = list.findIndex((c) => c.card.driverId === spent.driverId);
      if (i === -1) continue;
      const car = list[i];
      if (!car.grants) continue;
      const left: RaceGrants = { ...car.grants };
      delete left[spent.grant as keyof RaceGrants];
      list[i] = grantsAreEmpty(left) ? { ...car, grants: undefined } : { ...car, grants: left };
    }

    // EZRA INJURY TABLES — "Driver has a serious Injury and cannot contiue
    // racing today." He leaves the race here, AFTER the incident's own deltas
    // have moved everybody, because he might already have been retired by the
    // repair roll that follows being collected.
    for (const hurt of event.injuries ?? []) injuriesThisTurn.push(hurt);
    for (const id of event.injuredOutIds ?? []) {
      const i = w.order.findIndex((c) => c.card.driverId === id);
      if (i === -1) continue; // already out — the repair roll got him first
      const car = w.order[i];
      const livePosition = i + 1;
      w.order.splice(i, 1);
      noteVacancy(w, id, livePosition, 'injury', car.card.name);
      const stripped = voidCarChips(w, car, 'out of the race — chips are void (p.xv)');
      w.retired.push({ ...stripped, status: 'out', outReason: 'injury', outTurn: turn, position: livePosition });
    }
  };

  // -------------------------------------------------------------------------
  // RESERVE CHIPS — EZRA-S2 "Extra Chips in Excess of Two" (D-EZ-28)
  //
  // "When all active PC's are expended, he must activate a Reserve PC at the
  // beginning of the next turn (PRIOR TO ANY ACTIONS IN THAT TURN)."
  //
  // So this is the first thing that happens, ahead even of the trait awards and
  // the Race Start Event prelude — the sheet says "prior to any actions" and
  // means it. Note "MUST": it is not a choice, which is what lets a solitaire
  // engine run it without asking anybody. No dice are spent.
  // -------------------------------------------------------------------------
  for (let i = 0; i < w.order.length; i++) {
    const got = activateReserve(w.order[i]);
    if (!got.activated) continue;
    w.order[i] = got.car;
    record({
      eventType: 'reserve-chip',
      momentTag: 'chip.reserve-activated',
      ruleCitation: CITE_RESERVE,
      actorIds: [w.order[i].card.driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'active chips', value: 1 },
        { name: 'still in reserve', value: reserveChipsOf(w.order[i]) },
      ],
      dice: [],
      description:
        `${w.order[i].card.name} is out of chips and brings one up from reserve before the turn starts.`,
    });
  }

  // -------------------------------------------------------------------------
  // TRAIT CHIPS — EZRA-S2 §4.0 [FN] Finisher and [ST] Short Track Expert
  //
  // Both traits hand their driver a bonus performance chip at one fixed moment
  // in the race, and both are worded "at the start of the turn" / "with five
  // turns remaining" — a moment, not a roll. No dice are consumed here, which
  // is deliberate: a rule that costs a die repaints every seeded race that
  // follows it, and neither sheet asks for one.
  //
  // It runs BEFORE the Race Start Event prelude because "at the start of the
  // 1st turn" is the start of the whole turn, prelude included. A driver handed
  // a chip on a restart turn can spend it on the restart.
  // -------------------------------------------------------------------------
  for (const award of traitChipAwards(w.order, { turn, totalTurns, category: track.category })) {
    const i = w.order.findIndex((c) => c.card.driverId === award.driverId);
    if (i === -1) continue;
    // A trait chip takes an active seat and displaces an ORDINARY chip to
    // reserve if the pile is full (D-EZ-28), so §4.0's "not lost" is never in
    // conflict with the reserve rule's "lost on losing track position".
    const given = awardTraitChip(w.order[i], i + 1);
    w.order[i] = given.car;
    w.chipDeltas.push({ driverId: award.driverId, delta: +1, reason: award.reason });
    if (!given.usable) {
      w.chipHolds.push({ driverId: award.driverId, chips: 1, restored: false });
    }
    record({
      eventType: 'trait-chip',
      momentTag: given.usable ? 'chip.trait-award' : 'chip.trait-held',
      ruleCitation: given.usable ? award.citation : `${award.citation} + ${CITE_TRAIT_CHIPS}`,
      actorIds: [award.driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'trait', value: 0, note: award.trait },
        { name: 'performance chip', value: +1, note: given.usable ? 'in hand' : 'held — unusable down here' },
      ],
      dice: [],
      description: given.usable
        ? `${w.order[i].card.name} banks his ${award.label} performance chip.`
        : `${w.order[i].card.name} earns his ${award.label} performance chip, but he cannot spend it ` +
          `from where he is running — it keeps until he climbs back out.`,
    });
  }

  // -------------------------------------------------------------------------
  // PRELUDE — the EZRA BOTTOM GROUP CHURN (EZRA-S2 §3.12, D-EZ-29)
  //
  // "It is not a turn type but rather a Prelude Action that will precede any
  // turn if allowed by race circumstances and a random dice roll."
  //
  // IT RUNS AHEAD OF EVERY OTHER PRELUDE, INCLUDING THE RACE START EVENT, and
  // the sheet says so outright rather than leaving it to be reasoned about:
  // "When a BGC is activated, all other aspects of that turn as they affect the
  // Top, Middle and Bottom Groups, including a Bottom Group Surge, are resolved
  // after the BGC has completed." An RSE is one of those aspects.
  //
  // It sits AFTER the reserve-chip activation and the trait chip awards, which
  // are not aspects of the turn at all — one is "prior to any actions in that
  // turn" by its own words (D-EZ-28) and the other is a moment rather than a
  // roll, and neither costs a die.
  // -------------------------------------------------------------------------
  let churn: BottomGroupChurnResult | null = null;
  /**
   * Cautions raised INSIDE the Churn, held back until the turn is complete.
   *
   * "Yellow Flags that arise from the Churn action are deferred until the
   * Regular or Final Turn is completed." / "Yellow flags during the BGC do not
   * take effect until the end of the Regular Turn that follows the BGC."
   *
   * Said twice, in two different sections, which is as close to emphasis as
   * this document gets. So a Churn caution must not be visible to the rest of
   * the turn: not to the challenge sequence (RB/vi halts challenges once the
   * yellow is out), and not to FILLING A GAP (RB/vi fills differently under
   * caution — promote the front of the next group rather than roll for it).
   * It is merged back in below, after the gap has been filled, which is the
   * moment "the Regular or Final Turn is completed".
   */
  let churnYellowCameOut = false;
  let churnYellowTurns = 0;
  if (options.churn && isChurnTurn({ isPitTurn: isPit, isYellowTurn: wasYellow })) {
    const tracker = options.churn;
    dice.phase = `turn ${turn} — BOTTOM GROUP CHURN prelude (EZRA-S2 §3.12)`;
    const trigger = rollChurnTrigger(dice);
    if (trigger.fired) {
      tracker.triggered += 1;
      const third = raceThird(turn, totalTurns);

      if (!tracker.allows(third, finalTurn)) {
        // "Ignore any additional BGCs that are rolled in an increment where a
        // BGC has already occurred." Recorded as an event rather than skipped
        // in silence: the die came up and a printed rule threw it away, and a
        // limit nobody can see fire is a limit nobody can tell is working.
        tracker.noteIgnored();
        record({
          eventType: 'bottom-group-churn',
          momentTag: 'churn.ignored',
          ruleCitation: CITE_BGC_INCREMENT,
          actorIds: [],
          positionDeltas: [],
          modifiers: [{ name: 'one-third increment', value: third, detail: 'already had its Churn' }],
          dice: [trigger.record],
          description:
            'The bottom group stirs again — but they have already had their churn in this stretch of the race, and it is waved off.',
        });
      } else {
        const result = resolveBottomGroupChurn({
          order: w.order,
          dice,
          // "the MM dice are used on the Track Card": the same reader the RACE
          // CAM phase uses, footnotes and all, so a late-race override that
          // turns TROUBLE into STAR is honoured in a Churn exactly as it is in
          // a normal turn.
          readCam: (roll) => {
            const read = readRaceCam(track, roll, { turn, totalTurns });
            return { result: read.result, printed: read.printed };
          },
          lapsDown: w.lapsDown,
          triggerRecord: trigger.record,
          // D-EZ-39 — the BG performance chip is spendable inside a Churn and
          // nowhere else. Same switch the quality challenges and the duels take,
          // so head-to-head play turns all three off together.
          useChips: options.useChips,
        });
        churn = result;
        record(result);
        tracker.note(third, finalTurn, result.occurred ? 'occurred' : 'aborted');

        // The Churn resolved every exchange against its own working copy, so
        // the array it hands back is authoritative — CARDS, not ids, because a
        // laps-down car who churned back onto the lead lap carries the cleared
        // flag on the object.
        w.order = [...result.order];

        // ── the deferred-yellow shield ───────────────────────────────────────
        const yellowBefore = yellowCameOut;
        const yellowTurnsBefore = yellowTurnsCalled;

        // "If the black die is a 6 play the BGC as duel ... The duel is
        // resolved using normal procedures. The duel ends the BGC." Run here
        // rather than inside the Churn module for the same reason the Race
        // Start Event does not run its own PROBLEM: `duel.ts` owns duels, and a
        // second copy of one is how two versions of a rule start to drift.
        if (result.duelInstigatorId && result.duelTargetId) {
          const view = viewState(state, w, turn);
          const duel = resolveDuel(
            view,
            { instigatorId: result.duelInstigatorId, targetId: result.duelTargetId },
            dice,
            { useChips: options.useChips },
          );
          absorb({ ...duel, momentTag: `churn-duel.${duel.momentTag.replace('duel.', '')}` }, 'trouble');
          applyChipDeltas(w, duel.chipDeltas);
          // RB/ix — "Resolve the TROUBLE result … only for the driver in
          // trouble", which is also exactly what §3.12 asks for: "resolved as
          // single car events in the same fashion as for duels (RB/ix). The
          // Advanced Trouble Matrix rule is not applied." `troubleForOneCar`
          // already withholds the matrix, so this is the same helper the
          // ordinary duel path uses rather than a second copy of it.
          if (duel.pendingTrouble && duel.pendingTrouble.scope === 'driver') {
            troubleForOneCar(duel.pendingTrouble.driverId, 'the loser of a Bottom Group Churn duel (EZRA-S2 §3.12)');
          }
          if (duel.pendingProblem) {
            absorb(
              resolveProblem({
                field: troubleField(),
                group: 'bottom',
                ...incidentArgs,
                driverId: duel.pendingProblem.driverId,
                cause: duel.pendingProblem.description,
                specified: duel.pendingProblem.specified,
              }),
              'problem',
            );
          }
        }

        // The triples (RB/vi) and triangles (RB/xvi) the Churn's challenges
        // raised. Resolved AFTER the last challenge rather than interrupting
        // the sequence: §3.12 prints "Problem and Trouble resolution" after the
        // whole challenge procedure and prints no interrupt of its own, and
        // RB/vi's "no further challenges this turn" hangs off a yellow flag
        // that a Churn defers out of existence for the rest of the turn.
        // Deferring the incidents is the only ordering that invents nothing.
        for (const pending of result.pendingProblems) {
          absorb(
            resolveProblem({
              field: troubleField(),
              group: 'bottom',
              ...incidentArgs,
              // "The Advanced Trouble Matrix rule is not applied." Nothing to
              // withhold: `resolveProblem` has never consulted the matrix —
              // EZRA scopes it to GROUP trouble and `problem.ts` does not read
              // it. The sentence bites on the TROUBLE side, where
              // `troubleForOneCar` already withholds it.
              driverId: pending.driverId,
              cause: pending.description,
                specified: pending.specified,
            }),
            'problem',
          );
        }

        churnYellowCameOut = yellowCameOut && !yellowBefore;
        churnYellowTurns = Math.max(0, yellowTurnsCalled - yellowTurnsBefore);
        yellowCameOut = yellowBefore;
        yellowTurnsCalled = yellowTurnsBefore;
        if (churnYellowCameOut) {
          record({
            eventType: 'bottom-group-churn',
            momentTag: 'churn.yellow-deferred',
            ruleCitation: CITE_BGC_DEFERRED_YELLOW,
            actorIds: [],
            positionDeltas: [],
            modifiers: [
              { name: 'caution turns', value: churnYellowTurns, detail: CITE_BGC_INCIDENTS },
              { name: 'takes effect', value: 'after this turn is completed' },
            ],
            dice: [],
            description:
              'There is a car in the wall out of the churn — but the flag stays green until this turn is in the books.',
          });
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // PRELUDE — the EZRA BOTTOM GROUP SURGE (EZRA-S2 §3.13)
  //
  // ⚠ OFF UNLESS EXPLICITLY SWITCHED ON. §3.13 prints no trigger face — "An BGS
  // is activated by a D10 roll on the EZRA PRELUDE TRIGGER TABLE (PTT)", with no
  // number and no table in the corpus — so the trigger is OPEN RULING D-EZ-35 and
  // `options.surge` is absent by default. When it is absent NO DIE IS ROLLED, so
  // a race run with the Surge off is byte-identical to a race run before the rule
  // existed. That is the whole reason the guess is safe to have written down.
  //
  // IT RUNS IMMEDIATELY AFTER THE CHURN AND BEFORE THE RACE START EVENT
  // (D-EZ-38). Churn-then-Surge is printed — "all other aspects of that turn as
  // they affect the Top, Middle and Bottom Groups, including a Bottom Group
  // Surge, are resolved after the BGC has completed" — and Surge-before-RSE is an
  // engine ordering ruling with no printed basis, taken so that the pair of
  // BOTTOM group preludes settle the bottom group once rather than being
  // interleaved with a field-wide one.
  //
  // "any Pit or Final race turn", and a YELLOW FLAG is NOT a bar (D-EZ-36): the
  // Churn's yellow exclusion rests on RB/xi stopping the RACE CAM, and a Surge
  // reads no race cam. It rolls the Driver Finder and nothing else, which is also
  // why there is no deferred-caution machinery here — no incident can arise from
  // it, so there is nothing to defer.
  // -------------------------------------------------------------------------
  let surge: BottomGroupSurgeResult | null = null;
  if (options.surge && isSurgeTurn({ isPitTurn: isPit, isFinalTurn: finalTurn })) {
    const tracker = options.surge;
    dice.phase = `turn ${turn} — BOTTOM GROUP SURGE prelude (EZRA-S2 §3.13, ⚠ D-EZ-35)`;
    const trigger = rollSurgeTrigger(dice, tracker.faces);
    if (trigger.fired) {
      tracker.triggered += 1;
      const result = resolveBottomGroupSurge({
        order: w.order,
        dice,
        lapsDown: w.lapsDown,
        triggerRecord: trigger.record,
      });
      // The ordering ruling, recorded on the ledger EXACTLY WHEN IT BITES. The
      // two preludes can only meet on a Final turn — a Churn cannot occur on a
      // Pit Turn and a Surge occurs only on a Pit or Final one — so this modifier
      // appears on the handful of events where the order was a decision rather
      // than a non-question.
      if (churn !== null) {
        result.modifiers.push({
          name: 'resolved after the Bottom Group Churn',
          value: 'same turn',
          detail: CITE_BGS_ORDER,
        });
      }
      surge = result;
      record(result);
      // Authoritative, CARDS not ids — a laps-down car who surged back onto the
      // lead lap carries the cleared flag and his restored trait chips on the
      // object, exactly as with the Churn.
      w.order = [...result.order];
      tracker.note(result);
    }
  }

  // -------------------------------------------------------------------------
  // PRELUDE — the EZRA RACE START EVENT (D-EZ-22)
  //
  // "It is not a turn type but rather a Prelude Action that will precede any
  // turn if allowed by race circumstances and a random dice roll." So it runs
  // FIRST, before the pit turn and before the race cam, and every one of its
  // notes says "This event is resolved prior to regular turn activity."
  // -------------------------------------------------------------------------
  let restart: RestartEventResult | null = null;
  let restartCancelsTurn = false;
  /** EZRA RS — the extra pre-turn duel, and who it put out of the rest of the turn. */
  let restartDuel: DuelResult | null = null;
  let restartDuelLoserId: string | null = null;
  if (options.restartTurn === true) {
    dice.phase = `turn ${turn} — RACE START EVENT prelude (EZRA-S2 §3.11)`;
    const trigger = rollRseTrigger(dice);
    if (trigger.fired) {
      restart = resolveRestartEvent({
        field: troubleField(),
        dice,
        restartExperts: options.restartExperts,
      });
      record(restart);

      // The positional consequences the notes spell out. Problem, trouble and
      // repair are NOT run here — they are run below by the modules that own
      // them, because a second copy of any of those is how two versions of a
      // rule start to drift.
      if (!restart.rsTraitSaved) {
        const deltas = restartPositionDeltas(restart, renumber(w.order).order, dice);
        if (deltas.deltas.length > 0) {
          applyDeltas(w, deltas.deltas, turn, 'trouble');
        }
        if (deltas.chipTo) {
          applyChipDeltas(w, [{ driverId: deltas.chipTo, delta: +1, reason: 'great restart (EZRA RSE Note 2)' }]);
        }
        if (restart.needsProblem) {
          absorb(
            resolveProblem({
              field: troubleField(),
              group: restart.group ?? 'top',
              ...incidentArgs,
              driverId: restart.driverId ?? undefined,
              cause: 'RACE START EVENT — Problem',
            }),
            'problem',
          );
        }
        if (restart.needsTrouble) {
          // "Process under normal procedures as if MM designated Trouble on
          // track card. This event cancels the regular turn action and ends the
          // turn."
          absorb(
            resolveTrouble({
              field: troubleField(),
              group: restart.group ?? 'top',
              ...incidentArgs,
              cause: 'RACE START EVENT — Trouble in the field',
            }),
            'trouble',
          );
          restartCancelsTurn = true;
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // PIT TURN (p.xi) — "If a YELLOW FLAG turn occurs on a PIT TURN, conduct the
  // pit turn as usual."
  // -------------------------------------------------------------------------
  if (isPit && !restartCancelsTurn) {
    dice.phase = `turn ${turn} — PIT TURN (RB/xi)`;
    pit = resolvePitTurn(viewState(state, w, turn), dice, {
      leaderSpendsChip: options.leaderSpendsChip,
      yellow: wasYellow,
      pitViolations: options.pitViolations,
      pitTrouble: options.pitTrouble,
      crewInjured: options.crewInjured,
      lapsDown: w.lapsDown,
    });
    record(...pit.events);
    w.order = [...pit.order];
    w.chipDeltas.push(...pit.chipDeltas);

    // The pit turn hands back a whole new order, so a car it retired (B2 row 12
    // on a d6 of 6) has simply vanished from that array. It has to be recorded
    // in `retired` explicitly or it would be scored as a finisher.
    // POST RACE INTERVIEW — a fast-pit grant that fires twice is not a grant.
    for (const spent of pit.fastPit.grantsSpent) {
      const i = w.order.findIndex((c) => c.card.driverId === spent.driverId);
      if (i === -1) continue;
      const car = w.order[i];
      if (!car.grants) continue;
      const left: RaceGrants = { ...car.grants };
      delete left[spent.grant as keyof RaceGrants];
      w.order[i] = grantsAreEmpty(left) ? { ...car, grants: undefined } : { ...car, grants: left };
    }

    for (const out of pit.retired) {
      if (w.retired.some((c) => c.card.driverId === out.driverId)) continue;
      const car =
        state.grid.find((c) => c.card.driverId === out.driverId) ??
        pit.order.find((c) => c.card.driverId === out.driverId);
      if (!car) continue;
      w.retired.push({ ...car, chips: 0, status: 'out', outReason: out.reason, outTurn: turn });
    }
    // B2 note * is race-long; hand the injuries back up to `race.ts`.
    if (options.crewInjured) {
      for (const id of pit.crewInjured) {
        if (!options.crewInjured.includes(id)) options.crewInjured.push(id);
      }
    }
    for (const pair of pit.feudIds) if (!w.fuming.includes(pair)) w.fuming.push(pair);
  } else if (wasYellow) {
    // ── THE LUCKY DOG (D-EZ-13) ────────────────────────────────────────────
    // "The Lucky Dog free pass is implemented at the BEGINNING of the Yellow
    // Flag Turn." Awarded before the caution narration so the ledger reads in
    // the order the table would be played.
    if (w.lapsDown) {
      const dog = awardLuckyDog(w.order, w.lapsDown, turn);
      if (dog.driverId) {
        record({
          eventType: 'lucky-dog',
          momentTag: 'yellow.lucky-dog',
          ruleCitation: dog.ruleCitation,
          actorIds: [dog.driverId],
          positionDeltas: dog.delta ? [dog.delta] : [],
          modifiers: [],
          dice: [],
          description: dog.description,
        });
      }

      // The wave-around runs after the Lucky Dog and only when switched on.
      if (options.waveAround) {
        dice.phase = `turn ${turn} — WAVE AROUND (EZRA laps-down)`;
        const waved = waveAround(w.order, w.lapsDown, dice);
        const got = waved.filter((r) => r.waved);
        if (waved.length > 0) {
          record({
            eventType: 'wave-around',
            momentTag: got.length > 0 ? 'yellow.wave-around' : 'yellow.wave-around.none',
            ruleCitation: CITE_LD.waveAround,
            actorIds: waved.map((r) => r.driverId),
            positionDeltas: waved.flatMap((r) => (r.delta ? [r.delta] : [])),
            modifiers: [],
            dice: waved.flatMap((r) => r.dice),
            description:
              got.length === 0
                ? 'The wave-around is offered and nobody takes it — every car left a lap down missed the roll.'
                : `${got.length} car${got.length === 1 ? '' : 's'} waved around and back onto the lead lap.`,
          });
        }
      }
    }

    // p.xi — "In a YELLOW FLAG turn, no RACE CAM action occurs."
    record({
      eventType: 'yellow-flag-turn',
      momentTag: 'turn.yellow',
      ruleCitation: 'RB/xi — YELLOW FLAG TURNS ("no RACE CAM action occurs representing the \'down time\' produced by a yellow caution flag")',
      actorIds: [],
      positionDeltas: [],
      modifiers: [{ name: 'caution turns still to burn', value: state.yellowTurnsRemaining - 1 }],
      dice: [],
      description:
        'The pace car is out and the field circulates under caution. Ten miles pass with nobody racing anybody.',
    });
  } else if (restartCancelsTurn) {
    // EZRA-RSE Note 3, "8-0 Trouble in the field" — "This event cancels the
    // regular turn action and ends the turn." No race cam is read at all.
    record({
      eventType: 'restart-event',
      momentTag: 'restart.turn-cancelled',
      ruleCitation: CITE_NOTE_3,
      actorIds: [],
      positionDeltas: [],
      modifiers: [{ name: 'regular turn', value: 'cancelled' }],
      dice: [],
      description: 'The restart came apart in the middle of the field, and that is the turn gone — no race cam.',
    });
  } else {
    // -----------------------------------------------------------------------
    // RACE CAM (p.ii)
    // -----------------------------------------------------------------------
    dice.phase = `turn ${turn} — RACE CAM (RB/ii)`;
    const roll = dice.raceCam();
    raceCam = readRaceCam(track, roll, {
      turn,
      totalTurns,
      qualityCount: (quality, group) =>
        group === 'duel'
          ? 0
          : renumber(w.order)
              .order.filter((c) => groupOf(c.position) === group || (group === 'top' && c.position === 1))
              .filter((c) => c.card.qualities.includes(quality) || c.tvQuality === quality).length,
    });

    const camDice: DiceRecord[] = [
      { kind: 'd6', dice: [roll.black], label: 'RACE CAM black die', faces: { black: roll.black } },
      {
        kind: '2d6-sum',
        dice: roll.metallic,
        total: roll.total,
        label: 'RACE CAM metallic total',
        flags: { doubles: roll.doubles },
      },
    ];
    const camModifiers: Modifier[] = [
      { name: 'track card', value: track.name },
      { name: 'group', value: raceCam.group, detail: 'RB/ii — the black die names the group; the LEADER is never selected' },
      { name: 'printed cell', value: raceCam.printed },
    ];
    if (raceCam.footnote) {
      camModifiers.push({
        name: raceCam.overridden ? 'track card footnote applied' : 'track card footnote (not in force)',
        value: raceCam.result,
        detail: raceCam.footnote,
      });
    }
    if (raceCam.doubles) {
      camModifiers.push({
        name: 'MULTIPLE EVENTS',
        value: raceCam.group === 'duel' ? 'extra FEUD/FUME' : 'extra PROBLEM',
        detail: 'RB/iii — "ANY time the metallic dice are \'doubles\' on the RACE CAM dice roll, you\'ll add an extra event to the turn."',
      });
    }

    record({
      eventType: 'race-cam',
      momentTag: `cam.${raceCam.group}.${raceCam.result.toLowerCase()}`,
      ruleCitation: 'RB/ii — RACE-CAM PHASE: ONE BLACK DIE AND TWO METALLIC DICE',
      actorIds: [],
      positionDeltas: [],
      modifiers: camModifiers,
      dice: camDice,
      description:
        `RACE CAM: black ${roll.black} to the ${raceCam.group.toUpperCase()}, metallic ${roll.total} — ` +
        `${raceCam.result}${raceCam.overridden ? ` (footnote: ${raceCam.footnote})` : ''}.`,
    });

    // ---- THE RESTART DUEL (EZRA-S2 §4.0 RS, D-EZ-23) ----------------------
    //
    // "Before the Normal turn events this driver has the option to duel anybody
    // within the same group." So it sits HERE — after the cam has named a group
    // (the rule needs it) and before the action phase runs.
    if (options.followsYellow === true && (options.restartExperts?.size ?? 0) > 0) {
      const live = renumber(w.order).order;
      const rsPositions = new Map<string, number>();
      live.forEach((c, i) => {
        if (options.restartExperts?.has(c.card.driverId)) rsPositions.set(c.card.driverId, i + 1);
      });
      const offer = restartDuelOffer({
        followsYellow: true,
        calledGroup: raceCam.group === 'duel' ? null : (raceCam.group as 'top' | 'middle' | 'bottom'),
        restartExpertPositions: rsPositions,
        groupOfPosition: (p) => (groupOf(p) === 'leader' ? 'top' : (groupOf(p) as 'top' | 'middle' | 'bottom')),
      });
      if (offer.instigatorId && (options.restartDuelPolicy ?? 'auto') === 'auto') {
        const view = viewState(state, w, turn);
        const target = defaultDuelTarget(view, offer.instigatorId);
        if (target) {
          const duel = resolveDuel(
            view,
            { instigatorId: offer.instigatorId, targetId: target.card.driverId },
            dice,
            { useChips: options.useChips },
          );
          restartDuel = duel;
          absorb({ ...duel, momentTag: `restart-duel.${duel.momentTag.replace('duel.', '')}` }, 'feud');
          applyChipDeltas(w, duel.chipDeltas);
          // p.ix — "Resolve the TROUBLE result … only for the driver in
          // trouble." The same helper the ordinary duel path uses, rather than a
          // second copy of it.
          if (duel.pendingTrouble && duel.pendingTrouble.scope === 'driver') {
            troubleForOneCar(duel.pendingTrouble.driverId, 'the loser of a restart duel (EZRA RS)');
          }
          // "If the driver fails the duel that completes their turn."
          if (!duel.won) restartDuelLoserId = offer.instigatorId;
        }
      }
    }

    // p.xv — on the final turn BOTTOM-group results are disregarded entirely.
    //
    // AMBIGUITY: the printed sentence covers the black 5 for a QUALITY call
    // explicitly ("check for the indicated quality in BOTH the MIDDLE and TOP
    // groups, starting with the MIDDLE group") and says nothing about a black 5
    // that reads PROBLEM, TROUBLE or RARE. Read here as: "disregard results for
    // the BOTTOM group" governs, so those are dropped, and only the quality case
    // is redirected upward.
    const bottomOnFinal = finalTurn && raceCam.group === 'bottom';
    const qualityCall = isQualityCall(raceCam.result);

    if (bottomOnFinal && !qualityCall) {
      record({
        eventType: 'final-turn-rule',
        momentTag: 'turn.final-bottom-disregarded',
        ruleCitation: 'RB/xv — "On the final turn ONLY, disregard results for the BOTTOM group on the RACE CAM dice roll."',
        actorIds: [],
        positionDeltas: [],
        modifiers: [{ name: 'disregarded', value: raceCam.result }],
        dice: [],
        description: `Final turn: the RACE CAM pointed at the BOTTOM group, so the ${raceCam.result} result is disregarded.`,
      });
    } else {
      // ---------------------------------------------------------------------
      // The action phase
      // ---------------------------------------------------------------------
      const groupsToResolve: CalledGroup[] =
        bottomOnFinal && qualityCall
          ? ['middle', 'top'] // p.xv — MIDDLE first, then TOP
          : raceCam.group === 'duel'
            ? []
            : [raceCam.group];

      if (bottomOnFinal && qualityCall) {
        record({
          eventType: 'final-turn-rule',
          momentTag: 'turn.final-black-five',
          ruleCitation: 'RB/xv — "if a black \'5\' is rolled, check for the indicated quality in BOTH the MIDDLE and TOP groups, starting with the MIDDLE group."',
          actorIds: [],
          positionDeltas: [],
          modifiers: [{ name: 'quality checked in', value: 'MIDDLE then TOP' }],
          dice: [],
          description: `Final turn, black 5: ${raceCam.result} is called in the MIDDLE group first, then the TOP group.`,
        });
      }

      // p.iii — MULTIPLE EVENTS. The extra PROBLEM may be taken first or last;
      // "The only exception would be if it's the FINAL turn of the race, in which
      // case you'll always resolve the PROBLEM first."
      const extraProblem = raceCam.doubles && raceCam.group !== 'duel';
      const runExtraProblem = () => {
        if (!extraProblem) return;
        const group = raceCam!.group === 'duel' ? 'top' : (raceCam!.group as CalledGroup);
        const problem = resolveProblem({
          field: troubleField(),
          group,
          ...incidentArgs,
          cause: 'MULTIPLE EVENTS — metallic doubles on the RACE CAM (p.iii)',
        });
        absorb(problem, 'problem');
      };

      /*
        ═══════════════════════════════════════════════════════════════════════
        THE BURST (✻) — D-EZ-75, and new to the engine with the 2025 card set.

        A cell wearing the burst resolves normally AND THEN a PROBLEM happens.
        Structurally it is the same thing the p.iii MULTIPLE EVENTS doubles rule
        above already does — one extra PROBLEM, ordered around the cell — which
        is why it is built here beside it rather than somewhere new.

        ⚠ CHICAGO GOES FIRST AND EVERYBODY ELSE GOES LAST. The 2025 roster notes
        say so outright: *"there's a special 'burst' symbol that signals a
        PROBLEM happens at the end of the turn. NOTE that there is a PROBLEM
        result that occurs on the #10 dice roll at Chicago. Here the PROBLEM is
        resolved at the start of the turn, BEFORE the TROUBLE result."* Chicago's
        burst cell IS a TROUBLE, so the order is the whole rule.

        The turn WINDOW — Iowa and Watkins Glen print "last ten turns" — is
        already applied by `readRaceCam`, which returns a null burst outside it.
        ═══════════════════════════════════════════════════════════════════════
      */
      const runBurst = () => {
        if (!raceCam?.burst) return;
        const group = raceCam.group === 'duel' ? 'top' : (raceCam.group as CalledGroup);
        absorb(
          resolveProblem({
            field: troubleField(),
            group,
            ...incidentArgs,
            cause: `BURST on the track card — ${raceCam.burst.text}`,
          }),
          'problem',
        );
      };

      if (extraProblem && finalTurn) runExtraProblem();
      if (raceCam.burst?.when === 'start-of-turn') runBurst();

      if (raceCam.group === 'duel') {
        resolveDuelPhase();
      } else {
        for (const group of groupsToResolve) {
          resolveCalledResult(group, raceCam.result);
        }
      }

      if (raceCam.burst?.when === 'end-of-turn') runBurst();
      if (extraProblem && !finalTurn) runExtraProblem();

      // ---- TRACK PERSONALITY (house rule D-EZ-83) -------------------------
      //
      // Only on a turn the RACE CAM gave to racing — a QUALITY call in a real
      // group — and only while the flag is still green: a turn that already
      // produced its own incident, or already has a caution out, is not asked
      // again. See `houseRules.ts` TRACK_TEMPER for the per-track numbers.
      if (
        house.trackPersonality &&
        qualityCall &&
        !bottomOnFinal &&
        raceCam.group !== 'duel' &&
        !yellowCameOut &&
        trackTemperFor(track.category) > 0
      ) {
        dice.phase = `turn ${turn} — TRACK TEMPER (house rule D-EZ-83)`;
        absorb(
          resolveTrackTemper({
            field: troubleField(),
            group: raceCam.group as 'top' | 'middle' | 'bottom',
            category: track.category,
            turn,
            totalTurns,
            escapeAdj: track.escapeAdj,
            repairAdj: track.repairAdj,
            lapsDown: options.lapsDown != null,
            lastLeadLapPosition: ldsgStart(w.order) - 1,
            dice,
          }),
          'trouble',
        );
      }
    }
  }

  // -------------------------------------------------------------------------
  // FILL A GAP (p.vi)
  // -------------------------------------------------------------------------
  if (w.vacated.leader + w.vacated.top + w.vacated.middle > 0) {
    dice.phase = `turn ${turn} — FILL A GAP (RB/vi)`;
    const gap = fillGaps({
      order: renumber(w.order).order,
      vacated: w.vacated as Vacancies,
      yellow: yellowCameOut,
      dice,
      cause: 'cars off the board',
      leavers: w.vacatedWhy,
    });
    record(gap);
    w.order = [...gap.order];
    w.chipDeltas.push(...gap.chipDeltas);
  }

  // -------------------------------------------------------------------------
  // THE CHURN'S DEFERRED CAUTION TAKES EFFECT (EZRA-S2 §3.12)
  //
  // "Yellow Flags that arise from the Churn action are deferred until the
  // Regular or Final Turn is completed." This is that moment, and it is
  // deliberately AFTER the gap has been filled: RB/vi fills a hole differently
  // under caution — promote the front of the next group rather than roll the
  // red die against each candidate's gas pedal — and a caution that is not out
  // yet must not decide how one is filled. From here the flag is real: the
  // caution clock below picks it up, and `race.ts` logs it and refuses to end
  // the race on it.
  // -------------------------------------------------------------------------
  if (churnYellowCameOut) {
    yellowCameOut = true;
    yellowTurnsCalled = Math.max(yellowTurnsCalled, churnYellowTurns);
  }

  // -------------------------------------------------------------------------
  // CONCLUDING THE TURN (p.xi) + LAPS LED (p.xv)
  // -------------------------------------------------------------------------
  // ---- LAPS LOST TO PACE (house rule D-EZ-85) ------------------------------
  //
  // On a long green run the leader laps the slowest cars at the back. After
  // `paceLapAfter(category)` straight green turns — this one included — and at
  // the end of every green turn after that until a caution, ONE car goes a lap
  // down: of the last `PACE_WINDOW` lead-lap cars in the BOTTOM group, the one
  // with the lowest GAS PEDAL (the card's own speed rating), the furthest back
  // on a tie. No die is rolled: the order and the cards decide it.
  //
  // It needs the laps-down sub-group (it has nowhere to put the car without
  // it), and it never acts on a turn that ended with the yellow out — the
  // caution froze the field. From then on the car is an ordinary LDSG car: the
  // Lucky Dog and the LDSG challenges are how he gets the lap back.
  if (
    house.lapsToPace &&
    w.lapsDown &&
    kind !== 'yellow' &&
    !wasYellow &&
    !yellowCameOut &&
    (options.greenRunBefore ?? 0) + 1 >= paceLapAfter(track.category)
  ) {
    const boundary = ldsgStart(w.order); // 1-based first lapped car
    const leadLapTail = w.order
      .slice(0, boundary - 1)
      .map((car, i) => ({ car, position: i + 1 }))
      .filter((x) => groupOf(x.position) === 'bottom' && !x.car.lapsDown)
      .slice(-PACE_WINDOW);
    if (leadLapTail.length > 0) {
      const slowest = [...leadLapTail].sort(
        (a, b) => a.car.card.gasPedal - b.car.card.gasPedal || b.position - a.position,
      )[0];
      const leaderName = w.order[0]?.card.name ?? 'the leader';
      record({
        eventType: 'lapped',
        momentTag: 'pace.lapped',
        ruleCitation: CITE_HOUSE.lapsToPace,
        actorIds: [slowest.car.card.driverId],
        positionDeltas: [{ driverId: slowest.car.card.driverId, from: slowest.position, to: null, placement: 'laps-down' }],
        modifiers: [
          { name: 'green-flag turns in a row', value: (options.greenRunBefore ?? 0) + 1 },
          { name: 'gas pedal', value: slowest.car.card.gasPedal, detail: `slowest of the last ${leadLapTail.length} cars on the lead lap` },
        ],
        dice: [],
        description: `${slowest.car.card.name} cannot hold the pace — ${leaderName} puts him a lap down.`,
      });
      applyDeltas(w, [{ driverId: slowest.car.card.driverId, from: slowest.position, to: null, placement: 'laps-down' }], turn, undefined);
    }
  }

  // THE BOUNDARY CLAMP. Placement rules can be taught the LDSG marker; swaps
  // cannot, and the pit turn's box re-order was measured trading a lapped car
  // for a lead-lap car. Applied once, at the end of the turn, so there is a
  // single place the invariant is made true and a single count of how often it
  // had to be. See `settleLapsDown`.
  if (w.lapsDown) {
    const settled = settleLapsDown(w.order);
    if (settled.moved > 0) {
      w.lapsDown.noteClamped(settled.moved);
      w.order = settled.order;
    }
  }

  const stamped = renumber(w.order);
  w.chipDeltas.push(...stamped.forfeits);
  w.chipHolds.push(...stamped.holds);
  let finalOrder = stamped.order;

  // EZRA-S2 §3.12 — "Cars in BG with PCs that move to Middle Group during a
  // Regular Turn lose their BGC PC(s) when exiting the BG."
  //
  // The LOSS itself is enforced by the representation, in `chips.ts`, on every
  // path that seats a card — see D-EZ-39 barrier (3). This is only the
  // NARRATION, attached here because `renumber` is the moment the turn's
  // positions stop being predictions. Filed as its own event and NOT as a chip
  // delta: a BG chip is "a token of a different color", and a -1 on the
  // performance chip ledger would say a driver lost something he never held.
  if (stamped.bgLosses.length > 0) {
    const nameById = new Map<string, string>(
      [...w.order, ...w.retired].map((c) => [c.card.driverId, c.card.name]),
    );
    record({
      eventType: 'bg-chip-scope',
      momentTag: 'chip.bg-lost',
      ruleCitation: CITE_BG_CHIP_LOST,
      actorIds: stamped.bgLosses.map((l) => l.driverId),
      positionDeltas: [],
      modifiers: stamped.bgLosses.map((l) => ({
        name: 'BG performance chip lost on leaving the BOTTOM group',
        value: -l.chips,
        note: `${nameById.get(l.driverId) ?? l.driverId}: P${l.from} → P${l.to}`,
      })),
      dice: [],
      description: stamped.bgLosses
        .map(
          (l) =>
            `${nameById.get(l.driverId) ?? l.driverId} climbs out of the bottom group and leaves his ` +
            `${l.chips === 1 ? 'bottom-group chip' : `${l.chips} bottom-group chips`} behind — ` +
            `${l.chips === 1 ? 'it is' : 'they are'} no good up here.`,
        )
        .join(' '),
    });
  }

  // EZRA-S2 §4.0 — trait chips that changed availability this turn. One event
  // for the lot, because it is bookkeeping the booth should be able to read,
  // not a moment of the race.
  if (w.chipHolds.length > 0) {
    const nameById = new Map<string, string>(
      [...w.order, ...w.retired].map((c) => [c.card.driverId, c.card.name]),
    );
    const line = describeHolds(w.chipHolds, (id) => nameById.get(id) ?? id);
    const restored = w.chipHolds.filter((h) => h.restored).length;
    record({
      eventType: 'trait-chip-scope',
      momentTag: restored > 0 ? 'chip.trait-restored' : 'chip.trait-held',
      ruleCitation: CITE_TRAIT_CHIPS,
      actorIds: w.chipHolds.map((h) => h.driverId),
      positionDeltas: [],
      modifiers: w.chipHolds.map((h) => ({
        name: h.restored ? 'trait chip returned' : 'trait chip held',
        value: h.chips,
        note: nameById.get(h.driverId) ?? h.driverId,
      })),
      dice: [],
      description: line ?? '',
    });
  }

  const leaderAfter = finalOrder[0]?.card.driverId ?? null;
  const leadChangedTo = leaderAfter !== null && leaderAfter !== leaderBefore ? leaderAfter : null;

  finalOrder = finalOrder.map((car, i) => {
    if (i !== 0) return car.consecutiveTurnsLed === 0 ? car : { ...car, consecutiveTurnsLed: 0 };
    return {
      ...car,
      consecutiveTurnsLed: car.card.driverId === leaderBefore ? car.consecutiveTurnsLed + 1 : 1,
      turnsLed: car.turnsLed + 1,
    };
  });

  // p.xv — "If a driver leads a race for 10 consecutive turns, he earns a bonus
  // 'laps led' performance chip."
  const leader = finalOrder[0];
  if (leader && leader.consecutiveTurnsLed > 0 && leader.consecutiveTurnsLed % 10 === 0) {
    // ⚠ THIS WROTE `chips + 1` DIRECTLY AND WAS THE LEAK IN THE CAP OF TWO.
    // Every other gain in the engine funnels through `applyChipDeltas` or
    // pitTurn's `addChips`, both of which cap; this one did not, so a leader on
    // two chips who led ten straight turns quietly went to three. The harness
    // reported a maximum of FOUR active chips on its first run and no test
    // failed, because no test asserted a ceiling that had never existed.
    const gained = gainChip(finalOrder[0], 1);
    finalOrder = finalOrder.map((c, i) => (i === 0 ? gained.car : c));
    if (gained.toReserve > 0) {
      w.chipHolds.push({ driverId: leader.card.driverId, chips: gained.toReserve, restored: false, reserve: true });
    }
    const delta: ChipDelta = {
      driverId: leader.card.driverId,
      delta: +1,
      reason: 'LAPS LED — ten consecutive turns in front (p.xv)',
    };
    w.chipDeltas.push(delta);
    record({
      eventType: 'laps-led',
      momentTag: 'chip.laps-led',
      ruleCitation: 'RB/xv — OPTIONAL AND ADVANCED RULES, "LAPS LED PERFORMANCE CHIP"',
      actorIds: [leader.card.driverId],
      positionDeltas: [],
      modifiers: [{ name: 'consecutive turns led', value: leader.consecutiveTurnsLed }],
      dice: [],
      description:
        `${leader.card.name} has led ${leader.consecutiveTurnsLed} straight turns and banks a bonus performance chip.`,
    });
  }

  const yellowRemaining = Math.max(0, state.yellowTurnsRemaining - (kind === 'green' ? 0 : 1)) + yellowTurnsCalled;

  // -------------------------------------------------------------------------
  // RAIN THREAT — D-EZ-88 (RARE RESULTS sheet)
  //
  // "At the END that turn and end of each succeeding turn, roll one die. If a
  // "1" or "6," then rain stops the event, and the current order is the final
  // race finish, if enough laps have been completed."
  //
  // ⚠ "ENOUGH LAPS" IS NOT DEFINED ANYWHERE IN THE CORPUS. Ruled as the sport's
  // own rule — a race is official once HALF its scheduled distance is complete
  // (`RAIN_OFFICIAL_FRACTION`). Rain that stops the event earlier is a delay:
  // the field waits it out and the race goes on, and the die keeps being rolled
  // every turn, because the weather has not gone anywhere.
  //
  // Not rolled at the end of a turn that is already the checkered flag — the
  // race is over before the sky is asked — nor after the race was called.
  // -------------------------------------------------------------------------
  const flagFallsAnyway = finalTurn && !wasYellow && !yellowCameOut && yellowRemaining === 0;
  if (rainCheckFrom !== undefined && turn >= rainCheckFrom && !flagFallsAnyway && !raceEnd.called) {
    dice.phase = `turn ${turn} — RAIN THREAT (RARE RESULTS sheet)`;
    const face = dice.d6();
    const rain = RAIN_STOPS_ON.includes(face);
    const official = turn >= totalTurns * RAIN_OFFICIAL_FRACTION;
    if (rain && official) {
      raceEnd.called = {
        turn,
        reason: 'rain',
        scheduledTurns: totalTurns,
        ruleCitation: CITE_RAIN,
        text: 'Rain stops the event.',
      };
    }
    record({
      eventType: rain ? 'race-called' : 'rain-check',
      momentTag: !rain ? 'rain.holds-off' : official ? 'race.rain-shortened' : 'rain.delay',
      ruleCitation: CITE_RAIN,
      actorIds: rain && official ? [finalOrder[0]?.card.driverId].filter((x): x is string => !!x) : [],
      positionDeltas: [],
      modifiers: [
        { name: 'rain die', value: face, detail: '1 or 6 — rain stops the event' },
        { name: 'turns complete', value: `${turn} of ${totalTurns}` },
        ...(rain && !official
          ? [{ name: 'not an official race yet', value: `under half distance (${RAIN_OFFICIAL_FRACTION * 100}%)` }]
          : []),
      ],
      dice: [{ kind: 'd6', dice: [face], label: 'RAIN THREAT die (1 or 6 = rain)' }],
      description: !rain
        ? `The clouds hang over the track, but the rain holds off — a ${face}.`
        : official
          ? `The rain arrives — a ${face} — and it is not stopping. Past halfway, so the race is official: ` +
            `${finalOrder[0]?.card.name ?? 'the leader'} wins it under the clouds, and the order stands as it is.`
          : `The rain arrives — a ${face} — but they are not halfway yet. The field sits out the shower and the race will go on.`,
    });
  }

  const nextState: RaceState = {
    ...state,
    turn: turn + 1,
    grid: finalOrder,
    retired: w.retired,
    yellowTurnsRemaining: yellowRemaining,
    feuds: w.feuds,
    fuming: w.fuming,
    ...(rainCheckFrom !== undefined ? { rainCheckFrom } : {}),
  };

  /**
   * D-018 — MARK THE HAND-ENTERED ROLLS, and why a per-TURN stamp is exact.
   *
   * The requirement is that "a roll that a human supplied must be
   * distinguishable from one the engine made, forever, in the saved record".
   * The face-by-face record of that is `Dice.stream`, written at the draw. This
   * is the second copy, on the rows a reader actually opens.
   *
   * It is stamped over the turn's whole window rather than roll by roll because
   * roll by roll is not available: the rule modules assemble their `DiceRecord`s
   * out of faces they were handed several draws earlier, so ledger order is not
   * draw order and about 8% of draws are never recorded at all. Measured, over
   * five 50-turn races, only about a third of recorded faces can be matched back
   * to the draw that produced them — the numbers are in the dice.ts header.
   *
   * The window IS exact, though, because it does not need matching: every face
   * drawn between `drawsAtTurnStart` and here belongs to this turn by
   * construction, and `entryOver` reports `mixed` rather than guessing when the
   * window straddles a hand→PRNG crossover. So the claim the stamp makes —
   * "every die behind this row was hand-entered" — is either true of all of them
   * or explicitly declared uncertain.
   */
  stampDiceEntry(events, dice.entryOver(drawsAtTurnStart, dice.stream.length));

  return {
    kind,
    turn,
    events,
    state: nextState,
    raceCam,
    pit,
    yellowCameOut,
    yellowTurnsCalled,
    leadChangedTo,
    collected,
    retiredThisTurn: w.retired
      .filter((c) => c.outTurn === turn)
      .map((c) => c.card.driverId),
    injuries: injuriesThisTurn,
    restart,
    restartDuel,
    churn,
    surge,
    chipHolds: w.chipHolds,
    careerConsequences,
    called: raceEnd.called,
  };

  // -------------------------------------------------------------------------
  // The action phase, by kind of call
  // -------------------------------------------------------------------------

  function resolveCalledResult(group: CalledGroup, result: RaceCamResult): void {
    if (result === 'RARE') {
      const rare = resolveRare({ field: troubleField(), group, ...incidentArgs, cause: 'RARE RESULT (p.iv)' });
      absorb(rare, 'problem');
      applyRareEffects(rare);
      return;
    }
    if (result === 'PROBLEM') {
      absorb(resolveProblem({ field: troubleField(), group, ...incidentArgs, cause: 'PROBLEM RESULT (p.iv)' }), 'problem');
      return;
    }
    if (result === 'TROUBLE') {
      absorb(resolveTrouble({ field: troubleField(), group, ...incidentArgs, cause: 'TROUBLE RESULT (p.v)' }), 'trouble');
      return;
    }
    if (!isQualityCall(result)) {
      throw new RangeError(`unhandled RACE CAM result "${result}"`);
    }

    // p.vi — the quality challenges, trailing car first.
    const problems: RuleEvent[] = [];
    dice.phase = `turn ${turn} — QUALITY CHALLENGES, ${group.toUpperCase()} group (RB/vi-vii)`;
    const sequence = resolveQualityChallenges(
      viewState(state, w, turn),
      { quality: result, group },
      dice,
      {
        useChips: options.useChips,
        lapsDown: w.lapsDown,
        // D-EZ-86 — clean air at the front on the start and every restart.
        ...(house.cleanAir && options.cleanAirTurn ? { cleanAir: CLEAN_AIR_BRAKE_BONUS } : {}),
        // EZRA RS — "If the driver fails the duel that completes their turn."
        ...(restartDuelLoserId ? { excludeChallengers: new Set([restartDuelLoserId]) } : {}),
        // p.vi — triples (and the p.xvi triangle) hand the challenger a PROBLEM.
        problemResolver: (pending) => {
          const problem = resolveProblem({
            field: troubleField(),
            group,
            ...incidentArgs,
            driverId: pending.driverId,
            cause: pending.description,
                specified: pending.specified,
          });
          markResolved([problem], dice.rolls);
          problems.push(problem);
          return { yellow: problem.yellowFlag, description: problem.description };
        },
      },
    );

    record(sequence);
    // The sequence resolved every challenge against its own working copy, so its
    // finished running order is authoritative for the cars still on the board.
    const byId = new Map(w.order.map((c) => [c.card.driverId, c]));
    const regainedLap = new Set(sequence.ldsgEscapes.filter(e => e.escaped).map(e => e.driverId));
    w.order = sequence.finalOrder.map((id) => {
      const car = byId.get(id);
      return car && regainedLap.has(id) ? { ...car, lapsDown: false } : car;
    }).filter((car): car is Car => !!car);
    applyChipDeltas(w, sequence.chipDeltas);

    // POST RACE INTERVIEW — "AUTO WIN ONE QUALITY CHALLENGE" is one time.
    // Struck off AFTER the order is rebuilt, because the rebuild is what puts
    // the car objects back on the board.
    for (const challenge of sequence.challenges) {
      if (!challenge.grantUsed) continue;
      const i = w.order.findIndex((c) => c.card.driverId === challenge.challengerId);
      if (i === -1) continue;
      const car = w.order[i];
      if (!car.grants) continue;
      const left: RaceGrants = { ...car.grants };
      delete left[challenge.grantUsed as keyof RaceGrants];
      w.order[i] = grantsAreEmpty(left) ? { ...car, grants: undefined } : { ...car, grants: left };
    }

    // …and then the PROBLEMs it raised are applied on top.
    for (const problem of problems) {
      absorb(problem as RuleEvent & { yellowFlag?: boolean }, 'problem');
    }

    // ---- EZRA drafting (EZRA-S2 §3.10) -----------------------------------
    //
    // "The DS makes his challenge attempt AFTER all other challenges have been
    // completed." So the draft phase runs HERE, last, against the order the
    // challenges left behind — not interleaved with them.
    //
    // A PROBLEM raised above counts as an interruption and cancels the draft:
    // "Any interruption of the turn prior to the DE due to RWBR or EZRA
    // procedure will cancel the DE." On any track that is not a super-speedway,
    // or in any field with no DS driver, this makes no rolls at all.
    dice.phase = `turn ${turn} — DRAFTING (MH drafting)`;
    const draft = runDraftPhase({
      state: viewState(state, w, turn),
      order: w.order,
      track,
      // `result` may be 'TV', which Race Week grants temporarily and which is
      // not one of the six printed qualities the line builder knows.
      quality: (result === 'TV' ? 'SKILL' : result) as Quality,
      calledGroup: group,
      dice,
      interrupted: problems.length > 0 || yellowCameOut,
      // D-EZ-84 — at a superspeedway every car can work the draft.
      everyoneDrafts: house.packRacing,
    });
    if (draft.events.length > 0) {
      w.order = draft.order;
      for (const e of draft.events) record(e);
    }

    // D-EZ-84, THE LEAD PACK. At a superspeedway the front of the field is in
    // the draft whatever part of the field the race cam is watching, so on a
    // quality turn that called the MIDDLE or BOTTOM group the TOP group gets the
    // same EZRA-S2 §3.10 Draft Event too — same gates (uninterrupted turn, lead
    // lap), same gas check, same tow. Only the trigger is extended; no new
    // mechanic. `runDraftPhase` makes no roll anywhere but a superspeedway.
    if (house.packRacing && group !== 'top' && isSuperSpeedway(track)) {
      const pack = runDraftPhase({
        state: viewState(state, w, turn),
        order: w.order,
        track,
        quality: (result === 'TV' ? 'SKILL' : result) as Quality,
        calledGroup: 'top',
        dice,
        interrupted: problems.length > 0 || yellowCameOut,
        everyoneDrafts: true,
      });
      if (pack.events.length > 0) {
        w.order = pack.order;
        for (const e of pack.events) {
          record({
            ...e,
            ruleCitation: `${e.ruleCitation} · ${CITE_HOUSE.packRacing} — the lead pack drafts on every quality turn`,
            modifiers: [...e.modifiers, { name: 'lead pack', value: 'drafting while the cam watched another group' }],
          });
        }
      }
    }
  }

  function resolveDuelPhase(): void {
    const call = callDuel(
      viewState(state, w, turn),
      { total: raceCam!.total, doubles: raceCam!.doubles, dice: raceCam!.metallic },
      dice,
    );
    record(call);

    for (const instigatorId of call.instigatorIds) {
      const view = viewState(state, w, turn);
      if (positionIn(w.order, instigatorId) === 0) continue;
      const target = defaultDuelTarget(view, instigatorId);
      // Presentation only: the table shows both cards on the duel's dice.
      const duel = dice.about(target ? [instigatorId, target.card.driverId] : [instigatorId], () =>
        resolveDuel(view, { instigatorId, targetId: target?.card.driverId ?? null }, dice, { useChips: options.useChips }),
      );
      record(duel);
      applyDeltas(w, duel.positionDeltas, turn, 'trouble');
      applyChipDeltas(w, duel.chipDeltas);

      // p.xvi — the triangle pre-empts everything else the duel would have done.
      if (duel.pendingProblem) {
        absorb(
          resolveProblem({
            field: troubleField(),
            group: 'top',
            ...incidentArgs,
            driverId: duel.pendingProblem.driverId,
            cause: duel.pendingProblem.description,
                specified: duel.pendingProblem.specified,
          }),
          'problem',
        );
        continue;
      }

      // p.ix — "Resolve the TROUBLE result … only for the driver in trouble."
      if (duel.pendingTrouble && duel.pendingTrouble.scope === 'driver') {
        troubleForOneCar(duel.pendingTrouble.driverId, 'the loser of a duel (p.ix)');
      }

      /**
       * POST RACE INTERVIEW — "FIRST DUEL- ROLL ON FEUD/FUME CHART- One first
       * Duel roll, immediately roll on Feud/Fume Chart for Racers involved."
       *
       * The effect had been granted, recorded on the carry and printed on the
       * ledger for three sessions, and NOTHING read it. This is the seam it
       * needed: the season knows he holds it, but only the turn loop knows
       * which duel is his FIRST, which is the entire content of the rule.
       *
       * Read literally, and each clause does work:
       *   • "immediately" — after the duel resolves, in the same turn, not
       *     queued for the end of it.
       *   • "for Racers involved" — the two men in the duel, whoever they are
       *     and whatever list they are or are not on. That is why
       *     `resolveFeudFume` grew a forced pair: selection is what the lists
       *     are for, and this result does its own selecting.
       *   • "One first Duel" — one time. The grant is struck the moment it
       *     fires, by the same `grantsUsed` path every other one-race grant
       *     uses, so it cannot go off twice.
       *
       * Either man may hold it; a duel has two racers in it and the sheet does
       * not say the holder must be the instigator.
       */
      const targetId = duel.targetId;
      if (targetId) {
        const holder = [instigatorId, targetId].find(
          (id) => w.order.find((c) => c.card.driverId === id)?.grants?.firstDuelFeudFume,
        );
        /**
         * ⚠ BOTH MEN MUST STILL BE ON THE BOARD, and this was found by counting
         * rather than by a test: the very first harness run threw
         * `driver drv-005 is not in the running order`.
         *
         * The duel resolves BEFORE this, and a duel sends its loser to TROUBLE
         * (p.ix), which can put him in the wall and out of the race on the same
         * turn. The Feud/Fume chart MOVES CARS ON THE BOARD, and a car that is
         * off the board cannot be moved — `pairing` throws rather than
         * pretending otherwise, which is the right behaviour and is what caught
         * this.
         *
         * DECLINED rather than reordered. Rolling the chart before the duel's
         * trouble would let one man be sent to trouble twice in one turn off a
         * single duel, which no sheet describes. The sheet says "immediately",
         * and immediately after a duel that ended somebody's afternoon there is
         * nothing left to roll for. The skip is COUNTED in the harness rather
         * than swallowed, so the rate is visible.
         */
        const bothRunning =
          holder !== undefined &&
          w.order.some((c) => c.card.driverId === instigatorId) &&
          w.order.some((c) => c.card.driverId === targetId);

        /*
          D-EZ-89 (d) — "ONE first Duel": a declined roll still SPENDS the grant.

          The holder search above only looks at the running order, so a holder
          the duel's own trouble put in the wall was never found, and a holder
          whose OPPONENT went out was found, declined and left holding the
          grant — which then fired on his SECOND duel. The sheet grants one
          first duel, not one duel that happens to leave both men running.
          So: whoever of the two held it, wherever he is now, this was his first
          duel and the grant is struck here, with a ledger line that says why
          no chart was rolled.
        */
        const anyHolder = [instigatorId, targetId].find((id) =>
          [...w.order, ...w.retired].some((c) => c.card.driverId === id && c.grants?.firstDuelFeudFume),
        );
        if (anyHolder && !(holder && bothRunning)) {
          const holderName =
            [...w.order, ...w.retired].find((c) => c.card.driverId === anyHolder)?.card.name ?? anyHolder;
          absorb(
            {
              eventType: 'interview-grant',
              momentTag: 'grant.first-duel-declined',
              ruleCitation:
                'RWBR_postraceinterview.pdf — "FIRST DUEL- ROLL ON FEUD/FUME CHART- One first Duel roll, ' +
                'immediately roll on Feud/Fume Chart for Racers involved." (D-EZ-89)',
              actorIds: [instigatorId, targetId],
              positionDeltas: [],
              modifiers: [
                {
                  name: 'interview grant — FIRST DUEL (declined)',
                  value: 'spent',
                  detail: `held by ${holderName}; his first duel of the race left only one of the two men running`,
                },
              ],
              dice: [],
              description:
                `${holderName} had a feud roll coming on his first duel, but that duel has already ended ` +
                `somebody's afternoon — there is nothing left to settle, and the chance is gone.`,
              grantsUsed: [{ driverId: anyHolder, grant: 'firstDuelFeudFume' }],
            },
            'feud',
          );
        }

        if (holder && bothRunning) {
          const forced = resolveFeudFume(viewState(state, w, turn), dice, {
            forcedPair: { a: instigatorId, b: targetId },
          });
          absorb(
            {
              ...forced,
              ruleCitation:
                `${forced.ruleCitation}; RWBR_postraceinterview.pdf — "FIRST DUEL- ROLL ON ` +
                `FEUD/FUME CHART- One first Duel roll, immediately roll on Feud/Fume Chart for Racers involved."`,
              modifiers: [
                ...forced.modifiers,
                {
                  name: 'interview grant — FIRST DUEL',
                  value: 'spent',
                  detail: `held by ${w.order.find((c) => c.card.driverId === holder)?.card.name ?? holder}; his first duel of the race`,
                },
              ],
              grantsUsed: [{ driverId: holder, grant: 'firstDuelFeudFume' }],
            },
            'feud',
          );
          // D-EZ-89 (a) — the Feud/Fume TROUBLE rows (2, 3, 4, 12) hand whole
          // GROUPS to the trouble module, and this path only ever looked for a
          // single driver, so a forced feud's track trouble was silently
          // dropped. Same helper as the ordinary feud path below, so the two
          // cannot drift again.
          feudTrouble(forced.pendingTrouble, 'a feud that came out of a first duel (RB/x)');
        }
      }
    }

    // p.ix/p.x — a 7 or metallic doubles adds a FEUD/FUME to the turn.
    if (call.feudFumeTriggered) {
      dice.phase = `turn ${turn} — FEUD / FUME (RB/x)`;
      const feud = resolveFeudFume(viewState(state, w, turn), dice);
      absorb(feud, 'feud');
      feudTrouble(feud.pendingTrouble, 'a feud that spilled onto the race track (p.x)');
    }
  }

  /**
   * D-EZ-88 — THE RARE RESULTS SIDE EFFECTS, APPLIED.
   *
   * `rare.ts` resolves everything the board spells out as a move of cards and
   * hands the rest back as `pendingEffects`, with its drivers isolated. For a
   * year those were computed and dropped. Each one below is applied exactly as
   * the RARE RESULTS sheet prints it, and nothing is added where it prints
   * nothing:
   *
   *   RAIN THREAT          "Roll two dice, add to determine how many turns from
   *                         current turn when rain could begin to fall. At the
   *                         END that turn and end of each succeeding turn, roll
   *                         one die. If a "1" or "6," then rain stops the event,
   *                         and the current order is the final race finish, if
   *                         enough laps have been completed."  The 2d6 is rolled
   *                         by rare.ts; the per-turn die is rolled at the end of
   *                         `runTurn` (search RAIN THREAT below).
   *   lose all chips       SUPER RARE race-stoppage rows: "lose all performance
   *                         chips" — field-wide, every car on the board.
   *   reduce PIT grade     "Pit crew member inured in pit; reduce PIT grade by
   *                         one level." The isolated driver's crew, A→B…D→E, for
   *                         the rest of THIS race (the sheet prints no duration;
   *                         the race is the only scope the engine can see, and
   *                         nothing is carried to the next card).
   *   all lose a quality   "Track conditions deteriorate: all drivers lose SKILL
   *                         quality." Every running car, for the rest of the race.
   *   race stopped         A RED FLAG. The sheet prints no mechanic for the
   *                         stoppage itself beyond the chip loss on the same row,
   *                         so the event is recorded and NOTHING else is invented.
   *   evacuation           "cancellation of rest of event" — the race is called
   *                         at the end of this turn, classified as it stands.
   */
  function applyRareEffects(rare: RareResult): void {
    careerConsequences.push(...rare.careerConsequences);
    for (const pending of rare.pendingEffects) {
      const effect = pending.effect;
      switch (effect.type) {
        case 'rainThreat': {
          const away = typeof effect.turnsUntilRain === 'number' ? effect.turnsUntilRain : null;
          if (away === null) break;
          const from = turn + away;
          rainCheckFrom = rainCheckFrom === undefined ? from : Math.min(rainCheckFrom, from);
          record({
            eventType: 'rare-effect',
            momentTag: 'rare.rain-threat.set',
            ruleCitation: CITE_RAIN,
            actorIds: [],
            positionDeltas: [],
            modifiers: [
              { name: 'turns until rain could fall', value: away },
              { name: 'first rain check', value: `end of turn ${from}` },
            ],
            dice: [],
            description:
              `Weather on the radar. Rain could reach the track in ${away} turn${away === 1 ? '' : 's'} — ` +
              `from the end of turn ${from} on, every turn is a gamble with the sky.`,
          });
          break;
        }
        case 'loseAllPerformanceChips': {
          let lost = 0;
          for (let i = 0; i < w.order.length; i++) {
            const before = w.chipDeltas.length;
            w.order[i] = voidCarChips(w, w.order[i], 'RARE RESULT — "lose all performance chips" (field-wide)');
            if (w.chipDeltas.length > before) lost++;
          }
          record({
            eventType: 'rare-effect',
            momentTag: 'rare.chips-lost',
            ruleCitation: `${CITE_RARE_EFFECTS} — "lose all performance chips"`,
            actorIds: [],
            positionDeltas: [],
            modifiers: [{ name: 'cars that lost chips', value: lost }],
            dice: [],
            description:
              lost === 0
                ? 'Every performance chip on the board is wiped out — not that anybody was holding one.'
                : `Every performance chip on the board is wiped out — ${lost} car${lost === 1 ? '' : 's'} lose what they had banked.`,
          });
          break;
        }
        case 'raceStopped': {
          record({
            eventType: 'rare-effect',
            momentTag: 'rare.red-flag',
            ruleCitation: `${CITE_RARE_EFFECTS} — "Race stopped"`,
            actorIds: [],
            positionDeltas: [],
            modifiers: [
              {
                name: 'red flag',
                value: 'field stopped',
                detail: 'the sheet prints no mechanic for the stoppage beyond the chips on the same row — none is added (D-EZ-88)',
              },
            ],
            dice: [],
            description: `Red flag. ${rare.entryText.replace(/;.*$/, '')} — the field is stopped where it stands until the track is fixed.`,
          });
          break;
        }
        case 'eventCancelled': {
          if (!raceEnd.called) {
            raceEnd.called = {
              turn,
              reason: 'cancelled',
              scheduledTurns: totalTurns,
              ruleCitation: `${CITE_RARE_EFFECTS} — "cancellation of rest of event"`,
              text: rare.entryText,
            };
          }
          record({
            eventType: 'race-called',
            momentTag: 'race.cancelled',
            ruleCitation: `${CITE_RARE_EFFECTS} — "cancellation of rest of event"`,
            actorIds: [],
            positionDeltas: [],
            modifiers: [{ name: 'race ends after turn', value: turn }],
            dice: [],
            description: `${rare.entryText.replace(/\.$/, '')}. The race is over after this turn — the order stands as the final result.`,
          });
          break;
        }
        case 'lowerPitCrewGrade': {
          for (const id of pending.actorIds) {
            const i = w.order.findIndex((c) => c.card.driverId === id);
            if (i === -1) continue;
            const car = w.order[i];
            const from = car.card.pitCrewGrade;
            const to = lowerGrade(from, typeof effect.levels === 'number' ? effect.levels : 1);
            w.order[i] = { ...car, card: { ...car.card, pitCrewGrade: to } };
            record({
              eventType: 'rare-effect',
              momentTag: 'rare.pit-grade-lowered',
              ruleCitation: `${CITE_RARE_EFFECTS} — "reduce PIT grade by one level"`,
              actorIds: [id],
              positionDeltas: [],
              modifiers: [
                { name: 'pit crew grade', value: `${from} → ${to}`, detail: 'for the rest of this race' },
              ],
              dice: [],
              description:
                from === to
                  ? `${car.card.name}'s crew loses a man over the wall — and they were already the slowest grade on pit road.`
                  : `${car.card.name}'s crew loses a man over the wall. They are a grade ${to} crew for the rest of the day.`,
            });
          }
          break;
        }
        case 'allDriversLoseQuality': {
          const quality = String(effect.quality ?? '').toUpperCase() as Quality;
          if (!(QUALITIES as readonly string[]).includes(quality)) break;
          let affected = 0;
          for (let i = 0; i < w.order.length; i++) {
            const car = w.order[i];
            const had = car.card.qualities.includes(quality) || car.tvQuality === quality;
            if (!had) continue;
            affected++;
            w.order[i] = {
              ...car,
              card: {
                ...car.card,
                qualities: car.card.qualities.filter((q) => q !== quality),
                starred: car.card.starred.filter((q) => q !== quality),
              },
              tvQuality: car.tvQuality === quality ? null : car.tvQuality,
            };
          }
          record({
            eventType: 'rare-effect',
            momentTag: 'rare.quality-lost',
            ruleCitation: `${CITE_RARE_EFFECTS} — "all drivers lose ${quality} quality"`,
            actorIds: [],
            positionDeltas: [],
            modifiers: [
              { name: 'quality lost', value: quality, detail: 'every running car, for the rest of this race' },
              { name: 'cars that had it', value: affected },
            ],
            dice: [],
            description: `The track is going away. Nobody can lean on ${quality.toLowerCase()} out there now — ${affected} car${affected === 1 ? '' : 's'} just lost an edge.`,
          });
          break;
        }
        default:
          break;
      }
    }
  }

  /**
   * D-EZ-89 — a Feud/Fume result's TROUBLE, for BOTH feud paths.
   *
   * "TROUBLE means the feud has caused trouble on the track, and you'll treat
   * this as a TROUBLE Race Cam result for all drivers in the indicated group"
   * (p.x). `feudFume.ts` names the group(s) by where the named drivers sat.
   *
   * (b) THE LEADER IS PART OF THE TOP GROUP FOR TROUBLE. p.v: "for TROUBLE
   * results, the race LEADER is considered to be part of the TOP group" — and
   * `troubleFieldFromGrid` already seats him at the head of TOP. A feud whose
   * rabbit was leading used to name the group 'leader' and the TROUBLE was
   * skipped outright (`continue`); it is now the TOP group's TROUBLE, once,
   * even when the pair named both 'leader' and 'top'.
   *
   * A driver-scoped pending trouble (no printed row makes one today) is still
   * honoured the RB/ix way, one car.
   */
  function feudTrouble(pending: readonly PendingTrouble[], cause: string): void {
    const groups: Exclude<Group, 'leader'>[] = [];
    for (const p of pending) {
      if (p.scope === 'driver') {
        troubleForOneCar(p.driverId, cause);
        continue;
      }
      const g = p.group === 'leader' ? 'top' : p.group;
      if (!groups.includes(g)) groups.push(g);
    }
    for (const group of groups) {
      absorb(resolveTrouble({ field: troubleField(), group, ...incidentArgs, cause }), 'trouble');
    }
  }

  /** One car's ESCAPE check and repair roll, as a duel loser gets (p.ix). */
  function troubleForOneCar(driverId: string, cause: string): void {
    const position = positionIn(w.order, driverId);
    if (position === 0) return;
    const car = w.order[position - 1];
    // p.v — the LEADER is checked as part of the TOP group.
    const group = camGroupOfPosition(position);
    const field: TroubleField = { top: [], middle: [], bottom: [] };
    field[group] = [
      {
        driverId,
        name: car.card.name,
        position,
        escape: car.card.escape,
        pitCrewGrade: car.card.pitCrewGrade,
        chips: car.chips,
        ...(car.card.dnfExempt ? { dnfExempt: true } : {}),
      },
    ];
    // EZRA: "The Optional Trouble Adjustments are restricted to Group Trouble
    // events. They do not apply to individual driver trouble (Feuds, Driver
    // Incidents, etc.)" This is the one-car path — a lost duel, a driver
    // incident — so the matrix is withheld and base-game trouble runs.
    //
    // MEASURED: leaving it in was spending Chart B spaces on single-car
    // incidents, which are far more numerous than group ones. Chart B has only
    // three to six spaces for a whole race, so the card was resetting several
    // times a race and the observed trouble mix collapsed onto the space ratio
    // — Chart A's whole track-character table was being washed out.
    const { matrix: _withheld, ...soloArgs } = incidentArgs;
    absorb(resolveTrouble({ field, group, ...soloArgs, bigOne: false, cause }), 'trouble');
  }
}

/** Which group a 1-based position belongs to, as the RACE CAM names them. */
export function camGroupOfPosition(position: number): Exclude<Group, 'leader'> {
  const g = groupOf(position);
  return g === 'leader' ? 'top' : g;
}
